import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import apiClient from '../../services/apiClient';
import otcService from '../../services/otcService';
import equiposService from '../../services/equiposService';
import empresaService from '../../services/empresaService';
import ConfiguracionBase from '../configuracion/ConfiguracionBase';
import { ActionButton } from '../common/Buttons';
import SearchableSelectField from '../common/SearchableSelectField';
import SearchSuggestField from '../common/SearchSuggestField';
import SearchableCombo from '../common/SearchableCombo';
import EquivalenciasModal from '../EquivalenciasModal';
import EquivalenciasPrintTable from '../EquivalenciasPrintTable';
import { useToast } from '../common/Toast';
import { useConfirm } from '../common/ModalConfirmacion';
import { confirmarSalida, useAvisoAlCerrar } from '../../hooks/useSalidaProtegida';
import { REINTENTOS_MAX, REINTENTO_MS, esFalloDeRed } from '../../utils/fallosDeRed';
import { useAuth } from '../../hooks/useAuth';
import { getCentroCostoPreferidoDesdeEquipo } from '../../utils/centroCosto';
import { obtenerCatalogo } from '../../services/catalogoCache';
import useBusquedaRemota from '../../hooks/useBusquedaRemota';
import { resolveCompanyLogoUrl } from '../../utils/companyBranding';
import HelpPanel, { HelpToggleBtn } from '../common/HelpPanel';
import { MONEDAS_OT, fmtImporte, tieneMonedasMixtas, totalizar } from '../../utils/moneda';
import AdjuntosOtTab from './AdjuntosOtTab';
import ZonaAltaRapida from '../common/ZonaAltaRapida';
import { hoyArgentina, ZONA_ARGENTINA } from '../../utils/dateUtils';
import {
  WorkspacePage,
  WorkspaceHeader,
  BackButton,
  TitleArea,
  Title,
  Subtitle,
  StatusPill,
  HeaderActions,
  WorkspaceBody,
  MainColumn,
  SideColumn,
  Panel,
  PanelHead,
  PanelBody,
  TabPanel,
  ScrollArea,
  SummaryRow,
  SummaryTotalRow,
  NoticeBar,
  WorkspaceTabBar,
  ButtonLabel,
  WorkspaceActionsMenu,
  FloatingAutoSave,
} from '../planillas/WorkspaceLayout';

// Selector de moneda del resumen de costos: cambia la unidad en la que se
// muestran los totales, no los importes cargados en cada renglón.
const ChipsMonedaOtc = styled.div`
  display: flex;
  gap: 4px;
  margin-left: auto;
`;

const ChipMonedaOtc = styled.button`
  padding: 1px 8px;
  border-radius: 999px;
  border: 1px solid ${({ theme, $activo }) =>
    $activo ? theme.colors.primary : theme.colors.borderDefault};
  background: ${({ theme, $activo }) =>
    $activo ? theme.colors.primaryAlpha : 'transparent'};
  color: ${({ theme, $activo }) =>
    $activo ? theme.colors.primary : theme.colors.txtSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ $activo }) => ($activo ? 700 : 400)};
  cursor: pointer;
  line-height: 1.5;
`;

const AvisoMonedaMixta = styled.small`
  display: block;
  margin-top: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  line-height: 1.35;
`;

// Cuerpo de la tarjeta de datos generales (compacto, varias subfilas).
const DatosBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Fila de selectores principales (equipo / título / centro de costo).
const SelectoresRow = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1.3fr 1fr;
  gap: 8px 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// Grilla compacta con los datos de solo lectura del equipo.
const EquipoInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(135px, 1fr));
  gap: 4px 12px;
  padding: 8px 10px;
  border: 1px dashed ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.bgSecundario};
`;

// Fila de fechas.
const FechasRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px 12px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

// Contenido interno de una pestaña: zona de alta fija + tabla scrolleable.
// $flowNatural: solo la pestaña "Detalles" la usa en mobile/tablet vertical —
// ver Panel/MainColumn/TabPanel en WorkspaceLayout.jsx para el porqué. Las
// pestañas con tabla (ScrollArea) NO pasan esta prop: deben conservar su
// scroll interno acotado también en mobile.
const TabBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
  padding: 10px 12px;
  gap: 8px;

  ${({ $flowNatural, theme }) => $flowNatural && `
    @media (max-width: ${theme.breakpoints.md}) {
      flex: none;
      min-height: unset;
    }

    @media (min-width: ${theme.breakpoints.md}) and (max-width: 1024px) and (orientation: portrait) {
      flex: none;
      min-height: unset;
    }
  `}
`;

// Línea de subtotal al pie de una tabla.
const SubtotalLine = styled.div`
  flex-shrink: 0;
  font-size: 13px;
  text-align: right;

  strong { font-weight: ${({ theme }) => theme.typography.fontWeight.semibold}; }
`;

// Cuerpo del formulario de cierre de OTC (en panel lateral).
const CierreBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Contenedor de botones de acción del panel lateral.
const SideActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  button { width: 100%; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 12px;
    font-weight: 700;
  }

  input, select, textarea {
    border: 1px solid ${({ theme }) => theme.colors.borderDefault};
    background: ${({ theme }) => theme.colors.bgSecundario};
    color: ${({ theme }) => theme.colors.txtPrincipal};
    border-radius: 6px;
    padding: 8px;
    font-size: 13px;
    width: 100%;
    box-sizing: border-box;
  }

  textarea {
    resize: vertical;
    min-height: 56px;
    max-height: 220px;
  }
`;

const AddZone = styled.div`
  flex-shrink: 0;
  min-width: 0;
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 10px;

  @media print {
    display: none;
  }
`;

const Button = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ $primary, $danger, $success, theme }) => ($success ? theme.colors.success : $primary ? theme.colors.primary : $danger ? theme.colors.danger : theme.colors.transparent)};
  color: ${({ $primary, $danger, $success, theme }) => ($success || $primary || $danger ? theme.colors.bgPrincipal : theme.colors.txtPrincipal)};
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AutoSaveIndicator = styled.span`
  font-size: 11px;
  display: flex;
  align-items: flex-start;
  gap: 5px;
  color: ${({ $status, theme }) =>
    $status === 'saved' || $status === 'saved-persisted' ? theme.colors.success
    : $status === 'error' ? theme.colors.danger
    : theme.colors.txtSecundario};
  opacity: ${({ $status }) => $status === 'idle' ? 0 : $status === 'saved-persisted' ? 0.55 : 1};
  transition: opacity 0.4s;
  ${({ $status, theme }) => $status === 'error' ? `
    max-width: 420px;
    white-space: normal;
    line-height: 1.35;
    padding: 6px 10px;
    border-radius: ${theme.borderRadius.sm};
    background: ${theme.colors.dangerBg};
    border: 1px solid ${theme.colors.danger};

    i { margin-top: 1px; }
  ` : `
    white-space: nowrap;
    align-items: center;
  `}
`;

const TableCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    overflow-x: auto;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: ${({ theme }) => theme.colors.bgSecundario};
  }

  th {
    background: ${({ theme }) => theme.colors.bgSecundario};
    color: ${({ theme }) => theme.colors.txtPrincipal};
    border-bottom: 2px solid ${({ theme }) => theme.colors.borderDefault};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  th, td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
    padding: 5px 10px;
    text-align: left;
    font-size: 13px;
    line-height: 1.25;
    vertical-align: middle;
  }

  td {
    color: ${({ theme }) => theme.colors.txtSecondary};
  }

  td input:not([type='checkbox']),
  td select {
    min-height: 26px;
    padding: 4px 6px;
    font-size: 12px;
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.surface};
    border-color: ${({ theme }) => theme.colors.borderLight || theme.colors.borderDefault};
  }

  tbody .row-actions {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.12s ease;
  }

  tbody tr:hover .row-actions,
  tbody tr:focus-within .row-actions {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    tbody .row-actions {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const ReplacementLogWrap = styled.div`
  margin-top: 10px;
`;

const ReplacementLogTable = styled(Table)`
  th {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.txtSecondary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  label { font-size: 11px; font-weight: 700; text-transform: uppercase; color: ${({ theme }) => theme.colors.txtMuted}; }
  span, input, select, textarea { font-size: 13px; }
  input, select, textarea {
    border: 1px solid ${({ theme }) => theme.colors.borderDefault};
    background: ${({ theme }) => theme.colors.bgSecundario};
    color: ${({ theme }) => theme.colors.txtPrincipal};
    border-radius: 6px;
    padding: 6px 8px;
  }
`;

const ThemedInput = styled.input`
  width: 100%;
  border: 1px solid ${({ theme, $error }) => $error ? theme.colors.danger : theme.colors.borderDefault} !important;
  background: ${({ theme, $error }) => $error ? theme.colors.dangerBg : theme.colors.bgSecundario} !important;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
  box-sizing: border-box;
`;

const MutedText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.txtMuted};
`;

const DeleteIconButton = styled(ActionButton)`
  width: 28px;
  height: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const EqIconButton = styled.button`
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.success};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const PrintOnlyBlock = styled.div`
  display: none;

  @media print {
    display: block;
  }
`;

const estadoLabel = {
  abierta: 'Abierta',
  cerrada: 'Cerrada',
  cancelada: 'Cancelada',
};

const todayYmd = () => hoyArgentina();

async function fetchAllOptions(endpoint, params = {}) {
  let url = endpoint;
  let firstParams = { page_size: 10000, ...params };
  let all = [];
  let loops = 0;

  while (url && loops < 40) {
    const response = await apiClient.get(url, { params: firstParams });
    const payload = response.data;

    if (Array.isArray(payload)) {
      all = payload;
      break;
    }

    all = all.concat(Array.isArray(payload?.results) ? payload.results : []);
    url = payload?.next || null;
    firstParams = undefined;
    loops += 1;
  }

  return all;
}

const isValidYmd = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && (dt.getUTCMonth() + 1) === m && dt.getUTCDate() === d;
};

/**
 * Reconcilia lo que devolvió el servidor tras un guardado con lo que la persona
 * hizo en pantalla MIENTRAS ese guardado estaba en vuelo.
 *
 * - Fila que existe en las dos: si un campo cambió respecto de lo que se envió,
 *   gana el valor local (el servidor todavía no lo conoce).
 * - Fila local que no se había enviado (agregada durante el guardado): se
 *   conserva al final; el servidor no puede tenerla.
 *
 * `estado.pendiente` pasa a true si hubo cualquier diferencia, para que el
 * llamador programe otro guardado. `traducir` convierte referencias a filas
 * temporales (p. ej. la tarea de un repuesto) a su id real.
 */
const fusionarLista = ({ servidor, locales, enviadas, idMap, campos, estado, traducir = {} }) => {
  const resultado = servidor.map((s) => {
    const local = locales.find((l) => (idMap[l.id] ?? l.id) === s.id);
    const enviada = local && enviadas.find((x) => x.id === local.id);
    if (!local || !enviada) return s;
    const editados = campos.filter((c) => (local[c] ?? '') !== (enviada[c] ?? ''));
    if (editados.length === 0) return s;
    estado.pendiente = true;
    return editados.reduce((acc, c) => {
      const valor = traducir[c] ? traducir[c](local[c]) : local[c];
      return { ...acc, [c]: valor };
    }, s);
  });
  const nuevas = locales.filter((l) => !enviadas.some((x) => x.id === l.id));
  if (nuevas.length > 0) estado.pendiente = true;
  return [...resultado, ...nuevas];
};

const FormularioOTC = ({ otc = null, initialEquipo = null, initialMecanico = null, onSave, onCancel, modoMecanico = false }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // 'idle' | 'saving' | 'saved' | 'error'
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [autoSaveErrorMsg, setAutoSaveErrorMsg] = useState('');
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const autoSaveTimer = useRef(null);
  // Reintentos automáticos consumidos desde el último guardado exitoso.
  const reintentosRef = useRef(0);
  // Mutex síncrono del guardado: impide que dos guardados (autoguardado + manual,
  // o dos autoguardados encadenados) corran a la vez. Sin esto, si un guardado
  // está en vuelo y se dispara otro, el segundo lee el estado que TODAVÍA tiene
  // las tareas/repuestos con id `_temp_` (loadDetail aún no reemplazó los ids
  // reales) y los vuelve a crear → filas duplicadas. `savingInFlightRef` marca
  // que hay uno corriendo; `savePendingRef` acumula la petición de otro guardado
  // para ejecutarlo UNA sola vez al terminar el actual (coalescing).
  const savingInFlightRef = useRef(false);
  const savePendingRef = useRef(null); // null | { silent: boolean }
  // Último estado renderizado: permite leer, al terminar un guardado, lo que la
  // persona tipeó mientras el guardado estaba en vuelo.
  const estadoLocalRef = useRef(null);
  // Evita autoguardar durante la carga inicial de datos desde el servidor.
  const dataLoadedRef = useRef(false);
  const [currentOtcId, setCurrentOtcId] = useState(otc?.id || null);
  const [error, setError] = useState('');

  const [equipos, setEquipos] = useState([]);
  // Señales anti-truncado de los buscadores server-side: "hay más resultados"
  // y error visible (nunca dejar al usuario creyendo que un registro no existe)
  const [equipoSearchHasMore, setEquipoSearchHasMore] = useState(false);
  const [equipoSearchError, setEquipoSearchError] = useState(false);
  const [equipoLoadingMore, setEquipoLoadingMore] = useState(false);
  const [repuestoSearchHasMore, setRepuestoSearchHasMore] = useState(false);
  const [repuestoSearchError, setRepuestoSearchError] = useState(false);
  const [repuestoLoadingMore, setRepuestoLoadingMore] = useState(false);
  // Página actual y término vigente del scroll infinito de cada combo server-side
  const equipoSearchPageRef = useRef(1);
  const equipoSearchTermRef = useRef('');
  const repuestoSearchPageRef = useRef(1);
  const repuestoSearchTermRef = useRef('');
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [mecanicos, setMecanicos] = useState([]);
  // Solo mecánicos con usuario de portal para el selector de responsable
  const mecanicosConPortal = mecanicos.filter(m => m.usuario != null);
  const [repuestosCatalogo, setRepuestosCatalogo] = useState([]);
  const [soloRecomendados, setSoloRecomendados] = useState(false);
  // Refs para búsqueda server-side de equipos
  const equipoSearchRef = useRef(null);
  const formEquipoRef = useRef(String(otc?.equipo || initialEquipo || ''));
  // Refs para búsqueda server-side de repuestos
  const repuestoSearchRef = useRef(null);
  const newRepIdRef = useRef('');
  // Refs para scroll automático al panel de cierre
  const sideColumnRef = useRef(null);
  const closePanelRef = useRef(null);

  const [form, setForm] = useState({
    equipo: '',
    titulo: '',
    descripcion: '',
    fecha_apertura: todayYmd(),
    fecha_cierre_planificada: '',
    estado: 'abierta',
    centro_costo: '',
    mecanico_responsable: '',
    fecha_asignacion_mecanico: '',
    estado_reparacion: '',
    observaciones: '',
    gastos_extra: '',
  });

  const [estadosEquipo, setEstadosEquipo] = useState([]);
  const [estadosReparacion, setEstadosReparacion] = useState([]);
  const [estadoEquipoSeleccionado, setEstadoEquipoSeleccionado] = useState('');

  const [tareas, setTareas] = useState([]);
  const [repuestos, setRepuestos] = useState([]);
  const [repuestosAdicionales, setRepuestosAdicionales] = useState([]);
  // Si la organización no gestiona la entrega desde almacén, la columna Entregado de los
  // repuestos planificados se oculta: mostraría 0 en todas las filas y su presencia sirve
  // de indicador visual de si el control por almacén está activo.
  const [gestionaStockAlmacen, setGestionaStockAlmacen] = useState(true);
  const [otcMecanicos, setOtcMecanicos] = useState([]);
  const [gastosExtra, setGastosExtra] = useState([]);
  const [adjuntos, setAdjuntos] = useState([]);

  const [showEqModal, setShowEqModal] = useState(false);
  const [eqIdx, setEqIdx] = useState(null);
  const [equivalenciasCache, setEquivalenciasCache] = useState({});

  const [newTask, setNewTask] = useState({ tarea_catalogo: '' });
  const [taskSearch, setTaskSearch] = useState('');
  // Catálogo de tareas: se busca en el servidor en vez de bajarlo entero. Era el
  // pedido más pesado del formulario (~890 KB) y se repetía en cada OTC abierta.
  // La búsqueda alcanza todas las tareas del tenant, no sólo las que entrarían
  // en una primera página.
  const {
    items: tareasCatalogo,
    buscar: buscarTareas,
    cargarMas: cargarMasTareas,
    sumarItem: sumarTareaAlCatalogo,
    hasMore: tareasHasMore,
    loadingMore: tareasLoadingMore,
    error: tareasBusquedaError,
  } = useBusquedaRemota({ endpoint: '/tareas/', pageSize: 50, idSeleccionado: newTask.tarea_catalogo });
  const [newRep, setNewRep] = useState({ tarea: '', repuesto: '', cantidad_planificada: 1, cantidad_usada: '', costo_unitario: '' });

  const [newMec, setNewMec] = useState({ mecanico: '', horas_trabajadas: '', costo_hora: '' });
  const [newGasto, setNewGasto] = useState({ concepto: '', precio_unitario: '', cantidad: 1 });
  const [showClosePanel, setShowClosePanel] = useState(false);
  const [closeForm, setCloseForm] = useState({ fecha: todayYmd(), generarParte: false, odometro: '' });
  const [logoEmpresa, setLogoEmpresa] = useState('');
  const [showNuevoRepuesto, setShowNuevoRepuesto] = useState(false);
    const [showNuevaTarea, setShowNuevaTarea] = useState(false);
    const [showNuevoMecanico, setShowNuevoMecanico] = useState(false);
  const [showNuevoEstadoReparacion, setShowNuevoEstadoReparacion] = useState(false);
  const [activeTab, setActiveTab] = useState('detalles');

  const { toast } = useToast();
  const { confirmar } = useConfirm();
  const { hasPermission } = useAuth();
  const theme = useTheme();

  const locked = useMemo(() => form.estado !== 'abierta', [form.estado]);
  const canDelete = hasPermission('ordentrabajocorrectiva', 'delete');
  const puedeEditarFechasMantenimiento = hasPermission('fechasmantenimiento', 'change');
  // Moneda en la que se muestran los totales de la OTC. Los renglones pueden
  // estar en pesos y en dólares a la vez: cada uno se toma del equivalente que
  // el backend calculó con la cotización de su fecha, así el total es real.
  const [monedaVista, setMonedaVista] = useState('ARS');
  const fmtOtc = useCallback((v) => fmtImporte(v, monedaVista), [monedaVista]);
  const hayMonedasMixtas = useMemo(
    () => tieneMonedasMixtas(repuestos, repuestosAdicionales, otcMecanicos, gastosExtra),
    [repuestos, repuestosAdicionales, otcMecanicos, gastosExtra]
  );

  const totalManoObra = useMemo(
    () => totalizar(otcMecanicos, { base: 'costo_hora', cantidad: 'horas_trabajadas', moneda: monedaVista }),
    [otcMecanicos, monedaVista]
  );
  const totalGastosExtra = useMemo(
    () => totalizar(gastosExtra, { base: 'precio_unitario', cantidad: 'cantidad', moneda: monedaVista }),
    [gastosExtra, monedaVista]
  );
  const totalRepuestos = useMemo(
    () => totalizar(repuestos, { base: 'costo_unitario', cantidad: 'cantidad_usada', moneda: monedaVista }),
    [repuestos, monedaVista]
  );
  const totalRepuestosAdicionales = useMemo(
    () => totalizar(repuestosAdicionales, { base: 'costo_unitario', cantidad: 'cantidad_usada', moneda: monedaVista }),
    [repuestosAdicionales, monedaVista]
  );

  // Mapa repuesto_id → cantidad entregada como adicional (para mostrar aviso en pestaña planificados)
  const adicionalEntregadoByRepuesto = useMemo(() => {
    const map = {};
    repuestosAdicionales.forEach((r) => {
      const repId = String(typeof r.repuesto === 'object' ? r.repuesto?.id : r.repuesto);
      if (!repId) return;
      map[repId] = (map[repId] || 0) + Number(r.cantidad_entregada || 0);
    });
    return map;
  }, [repuestosAdicionales]);

  const otcReplacementLogRows = useMemo(() => {
    const rows = [];
    const pattern = /Reemplazo por equivalencia:\s*(.+?)\s*->\s*(.+?)\.?$/i;

    // Mapa código → {marca, descripcion} para enriquecer las filas
    const repInfoByCodigo = {};
    [...(repuestosCatalogo || []), ...(repuestos || [])].forEach((r) => {
      const cod = String(r?.codigo || r?.repuesto_codigo || '').trim().toUpperCase();
      if (cod && !repInfoByCodigo[cod]) {
        repInfoByCodigo[cod] = {
          marca: r.marca_nombre || r.repuesto_marca || '',
          descripcion: r.descripcion || r.repuesto_descripcion || '',
        };
      }
    });

    const enrichLabel = (codigo) => {
      const info = repInfoByCodigo[String(codigo || '').trim().toUpperCase()];
      return [codigo, info?.marca, info?.descripcion].filter(Boolean).join(' – ') || codigo || '-';
    };

    (repuestos || []).forEach((item) => {
      const observaciones = String(item?.observaciones || '');
      if (!observaciones.trim()) return;

      observaciones
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .forEach((line) => {
          const match = line.match(pattern);
          if (!match) return;

          const codigoOriginal = String(match[1] || '').trim();
          const codigoReemplazo = String(match[2] || '').trim();
          rows.push({
            original: enrichLabel(codigoOriginal),
            reemplazo: enrichLabel(codigoReemplazo),
            cantidad_planificada: Number(item?.cantidad_planificada || 0),
            cantidad_usada: Number(item?.cantidad_usada || 0),
          });
        });
    });

    return rows;
  }, [repuestos, repuestosCatalogo]);
  const totalGeneral = useMemo(
    () => totalRepuestos + totalRepuestosAdicionales + totalManoObra + totalGastosExtra,
    [totalRepuestos, totalRepuestosAdicionales, totalManoObra, totalGastosExtra]
  );

  const tareasDisponibles = useMemo(() => {
    // El filtro por término ya lo hizo el servidor. Acá sólo se descartan las
    // tareas que esta OTC ya tiene agregadas, para no ofrecerlas dos veces.
    const existentes = new Set(tareas.map((item) => String(item.catalogo_tarea_id || '')).filter(Boolean));
    const nombresExistentes = new Set(tareas.map((item) => String(item.titulo || '').trim().toLowerCase()).filter(Boolean));
    return tareasCatalogo.filter((item) => {
      if (existentes.has(String(item.id))) return false;
      if (nombresExistentes.has(String(item.nombre || '').trim().toLowerCase())) return false;
      return true;
    });
  }, [tareas, tareasCatalogo]);

  const equipoLabel = useCallback((eq) => `${eq.interno || ''}${eq.descripcion ? ' - ' + eq.descripcion : ''}`, []);
  const equipoFilter = useCallback((eq) => `${eq.interno || ''} ${eq.descripcion || ''} ${eq.marca_nombre || ''} ${eq.modelo_nombre || ''}`, []);
  const centroCostoLabel = useCallback((cc) => cc.nombre || '', []);
  const centroCostoFilter = useCallback((cc) => `${cc.nombre || ''}`, []);
  const repuestoLabel = useCallback((r) => [r.codigo, r.marca_nombre, r.descripcion].filter(Boolean).join(' - ') || '', []);
  const mecanicoLabel = useCallback((m) => m.nombre || '', []);

  const getEquipoCentroCostoId = useCallback((equipo) => {
    return getCentroCostoPreferidoDesdeEquipo(equipo);
  }, []);

  const EQUIPO_PAGE_SIZE = 20;
  const REPUESTO_PAGE_SIZE = 20;

  // Búsqueda server-side de equipos (debounced 300ms). Sin término mínimo: al
  // enfocar (searchTerm='') trae la primera página igual, para que la lista
  // se despliegue de entrada como en el resto del sistema. Scrolleando el
  // dropdown hasta el final se van pidiendo más páginas (handleEquipoLoadMore).
  const handleEquipoSearch = useCallback((searchTerm) => {
    clearTimeout(equipoSearchRef.current);
    const term = String(searchTerm || '').trim();
    equipoSearchRef.current = setTimeout(async () => {
      equipoSearchTermRef.current = term;
      equipoSearchPageRef.current = 1;
      try {
        // lite=true: los combos solo muestran interno/descripcion.
        const resp = await apiClient.get('/equipos/', {
          params: { ...(term ? { search: term } : {}), page_size: EQUIPO_PAGE_SIZE, page: 1, ordering: 'interno', lite: 'true' },
        });
        const results = resp.data?.results || resp.data || [];
        setEquipoSearchHasMore(Boolean(resp.data?.next));
        setEquipoSearchError(false);
        setEquipos(prev => {
          const selectedId = String(formEquipoRef.current || '');
          if (selectedId && !results.some(e => String(e.id) === selectedId)) {
            const sel = prev.find(e => String(e.id) === selectedId);
            return sel ? [sel, ...results] : results;
          }
          return results;
        });
      } catch {
        // Antes esto se tragaba el error y el usuario creía que el equipo no existía
        setEquipoSearchError(true);
        setEquipoSearchHasMore(false);
      }
    }, term ? 300 : 0);
  }, []);

  // Trae la página siguiente para el término vigente y la agrega al final
  // de la lista ya mostrada (scroll infinito del dropdown de equipo).
  const handleEquipoLoadMore = useCallback(async () => {
    const nextPage = equipoSearchPageRef.current + 1;
    setEquipoLoadingMore(true);
    try {
      const term = equipoSearchTermRef.current;
      const resp = await apiClient.get('/equipos/', {
        params: { ...(term ? { search: term } : {}), page_size: EQUIPO_PAGE_SIZE, page: nextPage, ordering: 'interno', lite: 'true' },
      });
      const results = resp.data?.results || resp.data || [];
      equipoSearchPageRef.current = nextPage;
      setEquipoSearchHasMore(Boolean(resp.data?.next));
      setEquipos(prev => {
        const existentes = new Set(prev.map(e => String(e.id)));
        return [...prev, ...results.filter(e => !existentes.has(String(e.id)))];
      });
    } catch {
      setEquipoSearchError(true);
    } finally {
      setEquipoLoadingMore(false);
    }
  }, []);

  // Búsqueda server-side de repuestos (debounced 300ms). Mismo criterio que
  // equipos: sin mínimo de caracteres, con scroll infinito para ver más.
  const handleRepuestoSearch = useCallback((searchTerm) => {
    clearTimeout(repuestoSearchRef.current);
    const term = String(searchTerm || '').trim();
    repuestoSearchRef.current = setTimeout(async () => {
      repuestoSearchTermRef.current = term;
      repuestoSearchPageRef.current = 1;
      try {
        const resp = await apiClient.get('/repuestos/', {
          params: {
            ...(term ? { search: term } : {}),
            ...(form.equipo ? { equipo: form.equipo } : {}),
            ...(form.equipo && soloRecomendados ? { solo_recomendados: 'true' } : {}),
            // Consumo: un discontinuado se ofrece solo mientras le quede stock.
            incluir_no_usar: 'con_stock',
            page_size: REPUESTO_PAGE_SIZE,
            page: 1,
          },
        });
        const results = resp.data?.results || resp.data || [];
        setRepuestoSearchHasMore(Boolean(resp.data?.next));
        setRepuestoSearchError(false);
        setRepuestosCatalogo(prev => {
          const selectedId = String(newRepIdRef.current || '');
          if (selectedId && !results.some(r => String(r.id) === selectedId)) {
            const sel = prev.find(r => String(r.id) === selectedId);
            return sel ? [sel, ...results] : results;
          }
          return results;
        });
      } catch {
        // Antes esto se tragaba el error y el usuario creía que el repuesto no existía
        setRepuestoSearchError(true);
        setRepuestoSearchHasMore(false);
      }
    }, term ? 300 : 0);
  }, [form.equipo, soloRecomendados]);

  // Trae la página siguiente para el término vigente (scroll infinito del
  // dropdown de repuestos).
  const handleRepuestoLoadMore = useCallback(async () => {
    const nextPage = repuestoSearchPageRef.current + 1;
    setRepuestoLoadingMore(true);
    try {
      const term = repuestoSearchTermRef.current;
      const resp = await apiClient.get('/repuestos/', {
        params: {
          ...(term ? { search: term } : {}),
          ...(form.equipo ? { equipo: form.equipo } : {}),
          ...(form.equipo && soloRecomendados ? { solo_recomendados: 'true' } : {}),
          incluir_no_usar: 'con_stock',
          page_size: REPUESTO_PAGE_SIZE,
          page: nextPage,
        },
      });
      const results = resp.data?.results || resp.data || [];
      repuestoSearchPageRef.current = nextPage;
      setRepuestoSearchHasMore(Boolean(resp.data?.next));
      setRepuestosCatalogo(prev => {
        const existentes = new Set(prev.map(r => String(r.id)));
        return [...prev, ...results.filter(r => !existentes.has(String(r.id)))];
      });
    } catch {
      setRepuestoSearchError(true);
    } finally {
      setRepuestoLoadingMore(false);
    }
  }, [form.equipo, soloRecomendados]);

  // Al togglear "solo recomendados" se re-dispara la búsqueda vigente (no en el
  // primer render, donde el desplegable de repuestos todavía no se abrió).
  const soloRecomendadosMountedRef = useRef(false);
  useEffect(() => {
    if (!soloRecomendadosMountedRef.current) {
      soloRecomendadosMountedRef.current = true;
      return;
    }
    handleRepuestoSearch(repuestoSearchTermRef.current);
  }, [soloRecomendados, handleRepuestoSearch]);

  const fetchEquivalenciasByRepuestoIds = useCallback(async (repuestoIds = [], obraId = null) => {
    if (!obraId || !Array.isArray(repuestoIds) || repuestoIds.length === 0) {
      return {};
    }

    // Antes pedía las equivalencias de a una por repuesto (incluso en tandas
    // paralelas, seguían siendo N requests HTTP — con 6 renglones ya eran ~5 s
    // sólo en esto). 'equivalencias-lote' resuelve el mismo prefetch para
    // todos los repuestos pedidos en una sola ida y vuelta.
    const cache = {};
    try {
      const resp = await apiClient.get('/repuestos/equivalencias-lote/', {
        params: { repuestos: repuestoIds.join(','), obra: obraId },
      });
      const resultados = resp.data?.resultados || {};
      for (const [repId, payload] of Object.entries(resultados)) {
        if (payload?.equivalentes) cache[repId] = payload.equivalentes;
      }
    } catch (err) {
      console.error('Error cargando equivalencias en lote:', err);
    }

    return cache;
  }, []);

  const loadOptions = async () => {
    // Solo catálogos pequeños — equipos, repuestos y tareas se cargan de forma diferida
    // Catálogos de referencia: cambian una vez cada tanto y se volvían a pedir
    // enteros en cada apertura del formulario.
    const [cc, mec, estados, estadosRep] = await Promise.all([
      obtenerCatalogo('centroscosto', () => fetchAllOptions('/centroscosto/', { ordering: 'nombre' })),
      obtenerCatalogo('mecanicos', () => fetchAllOptions('/mecanicos/', { ordering: 'nombre' })),
      obtenerCatalogo('estados-equipo', () => equiposService.obtenerEstados()),
      obtenerCatalogo('estados-reparacion', () => fetchAllOptions('/estados-reparacion/', { ordering: 'nombre' })),
    ]);
    setCentrosCosto(cc || []);
    setMecanicos(mec || []);
    setEstadosEquipo(Array.isArray(estados) ? estados : []);
    setEstadosReparacion(Array.isArray(estadosRep) ? estadosRep : []);
  };

  // Logo de empresa para cabecera de impresión obtenido desde la configuración persistida.
  // Cacheado igual que los catálogos de loadOptions: cambia rarísima vez y antes
  // se volvía a pedir en cada apertura de OTC (2 de los ~19 requests medidos).
  useEffect(() => {
    let mounted = true;
    const loadEmpresa = async () => {
      try {
        const data = await obtenerCatalogo('empresa', () => empresaService.obtener());
        const list = data.results || data || [];
        const first = Array.isArray(list) ? list[0] : list;
        if (mounted && first && first.logo) {
          setLogoEmpresa(first.logo);
        }
      } catch (e) {
        // noop
      }
    };
    loadEmpresa();
    return () => { mounted = false; };
  }, []);

  // Configuración de la organización: endpoint aparte del detalle de empresa porque ese
  // exige permiso de vista y quien completa la OT (mecánico) no lo tiene.
  useEffect(() => {
    let mounted = true;
    const loadConfiguracion = async () => {
      try {
        const data = await obtenerCatalogo('empresa-config', () => empresaService.obtenerConfiguracion());
        if (mounted) setGestionaStockAlmacen(data?.gestiona_stock_almacen !== false);
      } catch (e) {
        // Ante fallo se mantiene el comportamiento estricto (mismo default del backend).
      }
    };
    loadConfiguracion();
    return () => { mounted = false; };
  }, []);

  // `d` es la OTC ya traida del servidor. Se separa de loadDetail porque la
  // pagina contenedora (PaginaOTC) ya la pidio para poder montar este
  // formulario: volver a pedirla costaba ~1,7 s de mas en cada apertura.
  //
  // `conservar` ({ enviadas, idMap, pendiente }) se pasa SOLO al recargar tras
  // un guardado. Ese guardado tarda varios segundos (un PUT por tarea/repuesto/
  // mecánico) y la persona sigue tipeando mientras tanto: lo que tipeó después
  // de armar el payload no está en la respuesta del servidor, y reemplazar todo
  // por la respuesta lo pisaba con el valor viejo (las horas de una tarea
  // "volvían" solas) o hacía desaparecer filas recién agregadas. Se compara
  // contra lo que se envió y, si difiere, gana lo local y se pide otro guardado.
  const aplicarDetalle = async (d, conservar = null) => {
    const formServidor = {
      equipo: d.equipo || '',
      titulo: d.titulo || '',
      descripcion: d.descripcion || '',
      fecha_apertura: d.fecha_apertura || todayYmd(),
      fecha_cierre_planificada: d.fecha_cierre_planificada || '',
      fecha_cierre: d.fecha_cierre || '',
      estado: d.estado || 'abierta',
      centro_costo: d.centro_costo ? String(d.centro_costo) : '',
      mecanico_responsable: d.mecanico_responsable ? String(d.mecanico_responsable) : '',
      fecha_asignacion_mecanico: d.fecha_asignacion_mecanico || '',
      estado_reparacion: d.estado_reparacion ? String(d.estado_reparacion) : '',
      observaciones: d.observaciones || '',
      gastos_extra: d.gastos_extra ?? '',
    };

    // El detalle de la OTC (d) ya trae tareas/repuestos/mecanicos/gastos_detalle
    // anidados con el mismo serializer que exponen otc-tareas/otc-repuestos/
    // otc-mecanicos/otc-gastos-extra (ver OrdenTrabajoCorrectivaSerializer en el
    // backend). Volver a pedirlos por separado eran 5 de los ~19 requests que
    // hacían tardar ~15 s la apertura de una OTC. Sólo el detalle del equipo
    // (todos sus campos calculados: próximo mantenimiento, horómetro, etc.) no
    // viene anidado y sigue pidiéndose aparte.
    const repuestosTodos = Array.isArray(d.repuestos) ? d.repuestos : [];
    const eqData = d.equipo
      ? await equiposService.getDetalleEquipo(d.equipo).catch(() => null)
      : null;

    if (eqData) setEquipos([eqData]);
    let formFinal = formServidor;
    let tareasFinal = Array.isArray(d.tareas) ? d.tareas : [];
    let repuestosFinal = repuestosTodos.filter((r) => !r.es_adicional);
    let adicionalesFinal = repuestosTodos.filter((r) => r.es_adicional);
    let mecanicosFinal = Array.isArray(d.mecanicos) ? d.mecanicos : [];
    let gastosFinal = Array.isArray(d.gastos_detalle) ? d.gastos_detalle : [];

    if (conservar) {
      const { enviadas, idMap } = conservar;
      const locales = estadoLocalRef.current;
      const base = { idMap, estado: conservar };
      const camposRep = ['tarea', 'cantidad_planificada', 'cantidad_usada', 'costo_unitario', 'observaciones'];
      const traducir = { tarea: (v) => (v ? (idMap[v] ?? v) : v) };

      // Encabezado: sólo los campos que el payload envía y la persona puede editar.
      const camposForm = [
        'equipo', 'titulo', 'descripcion', 'fecha_apertura', 'fecha_cierre_planificada', 'estado',
        'centro_costo', 'mecanico_responsable', 'fecha_asignacion_mecanico', 'estado_reparacion', 'observaciones',
      ];
      const editadosForm = camposForm.filter((c) => (locales.form[c] ?? '') !== (enviadas.form[c] ?? ''));
      if (editadosForm.length > 0) {
        conservar.pendiente = true;
        formFinal = editadosForm.reduce((acc, c) => ({ ...acc, [c]: locales.form[c] }), formServidor);
      }

      tareasFinal = fusionarLista({
        ...base, servidor: tareasFinal, locales: locales.tareas, enviadas: enviadas.tareas,
        campos: ['titulo', 'descripcion', 'realizado', 'horas_hombre', 'observaciones'],
      });
      repuestosFinal = fusionarLista({
        ...base, servidor: repuestosFinal, locales: locales.repuestos, enviadas: enviadas.repuestos,
        campos: camposRep, traducir,
      });
      adicionalesFinal = fusionarLista({
        ...base, servidor: adicionalesFinal, locales: locales.repuestosAdicionales, enviadas: enviadas.repuestosAdicionales,
        campos: camposRep, traducir,
      });
      mecanicosFinal = fusionarLista({
        ...base, servidor: mecanicosFinal, locales: locales.otcMecanicos, enviadas: enviadas.otcMecanicos,
        campos: ['horas_trabajadas', 'observaciones'],
      });
      gastosFinal = fusionarLista({
        ...base, servidor: gastosFinal, locales: locales.gastosExtra, enviadas: enviadas.gastosExtra,
        campos: ['concepto', 'precio_unitario', 'cantidad'],
      });
    }

    setForm(formFinal);
    setTareas(tareasFinal);
    setRepuestos(repuestosFinal);
    setRepuestosAdicionales(adicionalesFinal);
    setOtcMecanicos(mecanicosFinal);
    setGastosExtra(gastosFinal);
    setAdjuntos(d.adjuntos || []);
    // Habilita el autoguardado una vez que los datos del servidor ya están en el estado.
    setTimeout(() => { dataLoadedRef.current = true; }, 0);
  };

  /** Recarga la OTC desde el servidor (tras guardar, cerrar o cancelar). */
  const loadDetail = async (id, conservar = null) => {
    const d = await otcService.obtener(id);
    await aplicarDetalle(d, conservar);
  };

  const handleSubirAdjunto = async (formData) => {
    const nuevo = await otcService.subirAdjunto(currentOtcId, formData);
    setAdjuntos(prev => [...prev, nuevo]);
  };

  const handleEliminarAdjunto = async (adjuntoId) => {
    await otcService.eliminarAdjunto(currentOtcId, adjuntoId);
    setAdjuntos(prev => prev.filter(a => a.id !== adjuntoId));
  };

  const handleEditarAdjunto = async (adjuntoId, formData) => {
    const actualizado = await otcService.editarAdjunto(currentOtcId, adjuntoId, formData);
    setAdjuntos(prev => prev.map(a => (a.id === adjuntoId ? actualizado : a)));
  };

  useEffect(() => {
    const run = async () => {
      dataLoadedRef.current = false;
      setLoading(true);
      setError('');
      try {
        await loadOptions();
        if (currentOtcId) {
          // Si la pagina contenedora ya trajo esta OTC, se usa esa: pedirla de
          // nuevo era traer dos veces exactamente lo mismo.
          if (otc && String(otc.id) === String(currentOtcId)) {
            await aplicarDetalle(otc);
          } else {
            await loadDetail(currentOtcId);
          }
        }
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || 'No se pudo cargar el formulario OTC.');
      } finally {
        setLoading(false);
      }
    };
    run();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOtcId]);


  useEffect(() => {
    if (!otc && initialEquipo) {
      setForm((prev) => ({ ...prev, equipo: String(initialEquipo) }));
      equiposService.getDetalleEquipo(initialEquipo)
        .then(eq => { if (eq) setEquipos([eq]); })
        .catch(() => {});
    }
  }, [otc, initialEquipo]);

  // Alta nueva desde el portal de mecánicos: la OTC se auto-asigna al mecánico logueado.
  useEffect(() => {
    if (!otc && initialMecanico) {
      setForm((prev) => ({ ...prev, mecanico_responsable: String(initialMecanico), fecha_asignacion_mecanico: todayYmd() }));
    }
  }, [otc, initialMecanico]);

  // Mantener refs sincronizados con las selecciones actuales
  useEffect(() => {
    formEquipoRef.current = form.equipo;
  }, [form.equipo]);

  useEffect(() => {
    newRepIdRef.current = newRep.repuesto;
  }, [newRep.repuesto]);

  useEffect(() => {
    if (!form.equipo) return;
    const equipoSel = equipos.find((e) => String(e.id) === String(form.equipo));
    if (!equipoSel) return;
    const centroEquipo = getEquipoCentroCostoId(equipoSel);
    setForm((prev) => {
      if (prev.centro_costo) return prev;
      return { ...prev, centro_costo: centroEquipo };
    });
  }, [form.equipo, equipos, getEquipoCentroCostoId]);

  // Ref que apunta siempre a la versión más reciente de handleSaveHeader
  // para evitar stale closure dentro del setTimeout del autoguardado.
  const handleSaveHeaderRef = useRef(null);

  // Worker crudo del guardado. NO llamar directo: usar handleSaveHeader (abajo),
  // que serializa las llamadas con el mutex para evitar duplicados.
  const runSaveHeader = async ({ silent = false } = {}) => {
    if (!form.equipo || !form.titulo.trim()) {
      if (!silent) toast.warning('Equipo y título son obligatorios.');
      return;
    }
    setSaving(true);
    if (silent) setAutoSaveStatus('saving');
    setError('');
    try {
      const payload = {
        equipo: Number(form.equipo),
        titulo: form.titulo,
        descripcion: form.descripcion || '',
        fecha_apertura: form.fecha_apertura || todayYmd(),
        fecha_cierre_planificada: form.fecha_cierre_planificada || null,
        estado: form.estado,
        centro_costo: form.centro_costo ? Number(form.centro_costo) : null,
        mecanico_responsable: form.mecanico_responsable ? Number(form.mecanico_responsable) : null,
        fecha_asignacion_mecanico: form.fecha_asignacion_mecanico || null,
        estado_reparacion: form.estado_reparacion ? Number(form.estado_reparacion) : null,
        observaciones: form.observaciones || '',
        // Se persiste total de gastos extra en el campo legado para mantener compatibilidad.
        gastos_extra: totalGastosExtra,
      };

      const saved = currentOtcId
        ? await otcService.actualizar(currentOtcId, payload)
        : await otcService.crear(payload);

      const otcId = saved.id;

      // Save all tasks, mapping temp IDs to real IDs
      const taskIdMap = {};
      for (const t of tareas) {
        const isTemp = typeof t.id === 'string' && t.id.startsWith('_temp_');
        const taskPayload = {
          orden: otcId,
          orden_item: t.orden_item,
          titulo: t.titulo,
          descripcion: t.descripcion || '',
          realizado: !!t.realizado,
          horas_hombre: t.horas_hombre === '' || t.horas_hombre == null ? null : t.horas_hombre,
          // Costo MO por tarea queda en desuso: se centraliza en mano de obra global.
          costo_mano_obra: null,
          observaciones: t.observaciones || '',
        };
        if (isTemp) {
          const created = await otcService.crearTarea(taskPayload);
          taskIdMap[t.id] = created.id;
        } else {
          await otcService.actualizarTarea(t.id, taskPayload);
          taskIdMap[t.id] = t.id;
        }
      }

      // Save all repuestos with mapped task IDs — usamos allSettled para acumular todos los errores
      const repResults = await Promise.allSettled(repuestos.map((r) => {
        const isTemp = typeof r.id === 'string' && r.id.startsWith('_temp_');
        const mappedTarea = r.tarea ? (taskIdMap[r.tarea] || r.tarea) : null;
        const repPayload = {
          orden: otcId,
          tarea: mappedTarea,
          repuesto: typeof r.repuesto === 'object' ? r.repuesto.id : r.repuesto,
          cantidad_planificada: Number(r.cantidad_planificada) || 1,
          cantidad_usada: r.cantidad_usada === '' || r.cantidad_usada == null ? null : Number(r.cantidad_usada),
          costo_unitario: r.costo_unitario === '' || r.costo_unitario == null ? null : r.costo_unitario,
          observaciones: r.observaciones || '',
          es_adicional: false,
        };
        return (isTemp ? otcService.crearRepuesto(repPayload) : otcService.actualizarRepuesto(r.id, repPayload))
          .then((creada) => {
            if (isTemp && creada?.id) taskIdMap[r.id] = creada.id;
            return { r, ok: true };
          })
          .catch((err) => ({ r, ok: false, err }));
      }));

      // Detectar repuestos con error de stock insuficiente (indexados por id para evitar comparación por referencia)
      const errorStockIds = new Set();
      for (const settled of repResults) {
        if (settled.status !== 'fulfilled') continue;
        const { r, ok, err } = settled.value;
        if (!ok) {
          const apiData = err?.response?.data;
          const msg = apiData?.cantidad_usada || apiData?.detail
            || (apiData && typeof apiData === 'object' ? Object.values(apiData).flat().find(v => typeof v === 'string') : null);
          if (msg) errorStockIds.add(r.id);
        }
      }
      const hayErrorStock = errorStockIds.size > 0;
      if (hayErrorStock) {
        setRepuestos(prev => prev.map((r) => {
          if (errorStockIds.has(r.id)) return { ...r, _errorStock: true };
          return r._errorStock ? { ...r, _errorStock: false } : r;
        }));
      }
      if (hayErrorStock) {
        throw new Error(
          'No se puede marcar usada una cantidad mayor a la entregada por almacén. ' +
          'Los repuestos con conflicto están resaltados en la tabla.'
        );
      }

      // Save all repuestos adicionales de almacén
      const repAdicResults = await Promise.allSettled(repuestosAdicionales.map((r) => {
        const isTemp = typeof r.id === 'string' && r.id.startsWith('_temp_');
        const repPayload = {
          orden: otcId,
          tarea: null,
          repuesto: typeof r.repuesto === 'object' ? r.repuesto.id : r.repuesto,
          cantidad_planificada: Number(r.cantidad_planificada) || 1,
          cantidad_usada: r.cantidad_usada === '' || r.cantidad_usada == null ? null : Number(r.cantidad_usada),
          costo_unitario: r.costo_unitario === '' || r.costo_unitario == null ? null : r.costo_unitario,
          observaciones: r.observaciones || '',
          es_adicional: true,
        };
        return (isTemp ? otcService.crearRepuesto(repPayload) : otcService.actualizarRepuesto(r.id, repPayload))
          .then((creada) => {
            if (isTemp && creada?.id) taskIdMap[r.id] = creada.id;
            return { r, ok: true };
          })
          .catch((err) => ({ r, ok: false, err }));
      }));

      const errorStockAdicIds = new Set();
      for (const settled of repAdicResults) {
        if (settled.status !== 'fulfilled') continue;
        const { r, ok, err } = settled.value;
        if (!ok) {
          const apiData = err?.response?.data;
          const msg = apiData?.cantidad_usada || apiData?.detail
            || (apiData && typeof apiData === 'object' ? Object.values(apiData).flat().find(v => typeof v === 'string') : null);
          if (msg) errorStockAdicIds.add(r.id);
        }
      }
      if (errorStockAdicIds.size > 0) {
        setRepuestosAdicionales(prev => prev.map((r) => {
          if (errorStockAdicIds.has(r.id)) return { ...r, _errorStock: true };
          return r._errorStock ? { ...r, _errorStock: false } : r;
        }));
        throw new Error(
          'No se puede marcar usada una cantidad mayor a la entregada por almacén en repuestos adicionales.'
        );
      }

      // Save all mecánicos
      for (const m of otcMecanicos) {
        const isTemp = typeof m.id === 'string' && m.id.startsWith('_temp_');
        const mecPayload = {
          orden: otcId,
          mecanico: typeof m.mecanico === 'object' ? m.mecanico.id : m.mecanico,
          horas_trabajadas: m.horas_trabajadas === '' || m.horas_trabajadas == null ? null : m.horas_trabajadas,
          observaciones: m.observaciones || '',
        };
        if (isTemp) {
          const creado = await otcService.crearMecanico(mecPayload);
          if (creado?.id) taskIdMap[m.id] = creado.id;
        } else {
          await otcService.actualizarMecanico(m.id, mecPayload);
        }
      }

      // Save all gastos extra detallados
      for (const g of gastosExtra) {
        const isTemp = typeof g.id === 'string' && g.id.startsWith('_temp_');
        const gastoPayload = {
          orden: otcId,
          concepto: g.concepto || '',
          precio_unitario: g.precio_unitario === '' || g.precio_unitario == null ? 0 : g.precio_unitario,
          cantidad: g.cantidad === '' || g.cantidad == null ? 0 : g.cantidad,
        };
        if (isTemp) {
          const creado = await otcService.crearGastoExtra(gastoPayload);
          if (creado?.id) taskIdMap[g.id] = creado.id;
        } else {
          await otcService.actualizarGastoExtra(g.id, gastoPayload);
        }
      }

      // Reload from server to get real IDs. Se desactiva el autoguardado durante la recarga
      // para evitar un ciclo inmediato al actualizar el estado con datos del servidor.
      dataLoadedRef.current = false;
      if (!currentOtcId) setCurrentOtcId(otcId);
      const conservar = {
        enviadas: { form, tareas, repuestos, repuestosAdicionales, otcMecanicos, gastosExtra },
        idMap: taskIdMap,
        pendiente: false,
      };
      try {
        await loadDetail(otcId, conservar);
        if (conservar.pendiente) {
          // Se tipeó algo mientras se guardaba: quedó en pantalla pero no en el
          // servidor. Se vuelve a guardar (el temporizador se arma recién ahora
          // porque scheduleAutoSaveText ignora ediciones con la carga en curso).
          clearTimeout(autoSaveTimer.current);
          autoSaveTimer.current = setTimeout(() => {
            autoSaveTimer.current = null;
            handleSaveHeaderRef.current?.({ silent: true });
          }, 1500);
        }
      } catch {
        // El guardado ya salió bien: lo que falló es volver a leer la orden.
        //
        // No se reactiva el autoguardado a propósito. La recarga es lo que
        // reemplaza los identificadores provisorios de las filas recién creadas
        // por los reales; sin ella, el próximo guardado volvería a crear esas
        // filas y quedarían duplicadas. Apagado es el estado correcto.
        //
        // Lo que faltaba era decirlo. Antes este camino dejaba `dataLoadedRef`
        // en false y salía por el catch de más abajo con un "no se pudo
        // guardar" que además era falso —el guardado había funcionado—, y el
        // autoguardado quedaba muerto para el resto de la sesión: la persona
        // seguía cargando tareas y repuestos en una pantalla que ya no
        // guardaba nada, sin un solo aviso. En una obra donde la señal se
        // corta justo después de un guardado, eso es media orden perdida.
        const aviso = 'Se guardó, pero no se pudo volver a leer la orden desde el '
          + 'servidor. Cerrala y volvé a abrirla para seguir editando: hasta '
          + 'entonces el guardado automático queda desactivado.';
        setAutoSaveErrorMsg(aviso);
        setAutoSaveStatus('error');
        if (!silent) {
          setError(aviso);
        } else {
          toast.warning(aviso);
        }
        return;
      }

      // Si el estado del equipo cambió, persistirlo junto con el guardado de la OTC
      if (estadoEquipoSeleccionado && form.equipo) {
        const originalEstado = String(equipoSeleccionado?.estado || '');
        if (estadoEquipoSeleccionado !== originalEstado) {
          try {
            await equiposService.actualizarEstado(Number(form.equipo), Number(estadoEquipoSeleccionado));
            const equipoActualizado = await equiposService.getDetalleEquipo(Number(form.equipo));
            setEquipos(prev => prev.map(e => String(e.id) === String(form.equipo) ? equipoActualizado : e));
          } catch (err) {
            console.error('Error al actualizar estado del equipo:', err);
          }
        }
      }

      setAutoSavedAt(new Date());
      reintentosRef.current = 0;
      if (silent) {
        setAutoSaveErrorMsg('');
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 2500);
      } else {
        toast.success('OTC guardada correctamente.');
      }

      if (onSave) {
        await onSave(saved);
      }
    } catch (e) {
      const apiData = e?.response?.data;
      const apiMsg = apiData?.detail
        || (apiData && typeof apiData === 'object' ? Object.values(apiData).flat().find(v => typeof v === 'string') : null)
        || e?.message
        || 'No se pudo guardar la OTC.';
      if (silent) {
        setAutoSaveErrorMsg(apiMsg);
        setAutoSaveStatus('error');
        // Insistir sólo mientras el problema sea de señal: si el servidor
        // contestó rechazando, el mismo envío va a dar el mismo rechazo. Nada
        // se perdió —el formulario sigue en pantalla con todo—, así que esto
        // no rescata datos: evita que la orden se quede sin guardar porque
        // nadie miró el indicador en rojo.
        if (esFalloDeRed(e) && reintentosRef.current < REINTENTOS_MAX) {
          reintentosRef.current += 1;
          clearTimeout(autoSaveTimer.current);
          autoSaveTimer.current = setTimeout(() => {
            autoSaveTimer.current = null;
            // Por el mutex, no directo: si hay un guardado en curso, este se encola.
            handleSaveHeaderRef.current?.({ silent: true });
          }, REINTENTO_MS);
        }
      } else {
        setError(apiMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  // Entrada única y serializada del guardado. Si ya hay uno en vuelo, no lanza
  // otro en paralelo: registra que hace falta re-guardar y lo ejecuta al final,
  // una sola vez. Un guardado explícito (silent:false) tiene prioridad sobre el
  // silencioso al momento de coalescer. Así las tareas/repuestos temporales se
  // persisten exactamente una vez y nunca se duplican.
  const handleSaveHeader = async ({ silent = false } = {}) => {
    if (savingInFlightRef.current) {
      // Coalescing: guardamos el pedido más "fuerte" (explícito gana a silent).
      const prev = savePendingRef.current;
      savePendingRef.current = { silent: prev ? (prev.silent && silent) : silent };
      return;
    }
    savingInFlightRef.current = true;
    try {
      await runSaveHeader({ silent });
    } finally {
      savingInFlightRef.current = false;
    }
    // Si mientras guardábamos se pidió otro guardado, ejecutarlo ahora (una vez).
    if (savePendingRef.current) {
      const next = savePendingRef.current;
      savePendingRef.current = null;
      await handleSaveHeader(next);
    }
  };

  // Mantener la ref sincronizada con la versión más reciente de handleSaveHeader.
  handleSaveHeaderRef.current = handleSaveHeader;
  estadoLocalRef.current = { form, tareas, repuestos, repuestosAdicionales, otcMecanicos, gastosExtra };

  /**
   * ¿Queda algo que no llegó al servidor?
   *
   * Va como función y no como booleano porque dos de las tres señales viven en
   * refs, que no disparan render: un booleano calculado en el render estaría
   * viejo justo en el momento en que se lo consulta.
   */
  const hayCambiosSinGuardar = () => (
    autoSaveStatus === 'error'
    || Boolean(autoSaveTimer.current)
    || Boolean(savingInFlightRef.current)
  );

  useAvisoAlCerrar(hayCambiosSinGuardar);

  const salirConAviso = async () => {
    if (!onCancel) return;
    if (await confirmarSalida(hayCambiosSinGuardar)) onCancel();
  };

  // Helpers de autoguardado por acción explícita.
  // Para texto libre: debounce 1.5s (evita guardar en cada tecla).
  // Para acciones discretas: inmediato (cancela cualquier timer pendiente y guarda ya).
  const scheduleAutoSaveText = useCallback(() => {
    if (!dataLoadedRef.current) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      // Se limpia al dispararse, igual que en `triggerAutoSaveNow`. Sin esto el
      // id del temporizador ya consumido quedaba guardado para siempre, y todo
      // lo que pregunta "¿hay algo esperando?" —el aviso de salida, el volcado
      // al desmontar— contestaba que sí aunque estuviera todo guardado.
      autoSaveTimer.current = null;
      handleSaveHeaderRef.current?.({ silent: true });
    }, 1500);
  }, []);

  const triggerAutoSaveNow = useCallback(() => {
    if (!dataLoadedRef.current) return;
    clearTimeout(autoSaveTimer.current);
    // setTimeout(0) permite que React aplique los setState pendientes antes de leer el estado en handleSaveHeader.
    autoSaveTimer.current = setTimeout(() => {
      autoSaveTimer.current = null;
      handleSaveHeaderRef.current?.({ silent: true });
    }, 0);
  }, []);

  // Al desmontar: si hay un timer de texto pendiente, ejecutar inmediatamente.
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
        // Último intento, y es lo único que se puede hacer: la pantalla ya no
        // existe para avisar si falla. Se traga el rechazo para no dejar una
        // promesa suelta, pero el aviso de salida (`useAvisoAlCerrar` +
        // `salirConAviso`) es lo que de verdad cubre este caso: para llegar
        // acá con algo sin guardar hay que haber confirmado que se sale igual.
        Promise.resolve(handleSaveHeaderRef.current?.({ silent: true })).catch(() => {});
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeEstado = async (target) => {
    if (!currentOtcId) return;
    try {
      // El interceptor global del apiClient maneja la confirmación de repuestos sin
      // devolver (409 requiere_confirmacion) y reintenta forzando si el usuario acepta.
      let updated;
      if (target === 'cerrada') {
        const fechaInput = closeForm.fecha;
        if (!isValidYmd(fechaInput)) {
          toast.warning('Fecha inválida.');
          return;
        }
        if (fechaInput > todayYmd()) {
          toast.warning('La fecha de cierre no puede ser posterior a hoy.');
          return;
        }
        updated = await otcService.cerrar(currentOtcId, {
          fecha_cierre: fechaInput,
          generar_parte_diario: closeForm.generarParte,
          odometro_realizado: closeForm.odometro || '',
        });
        setShowClosePanel(false);
        toast.success('OTC cerrada correctamente.');
      } else if (target === 'cancelada') {
        const ok = await confirmar({ titulo: 'Cancelar OTC', mensaje: 'La OTC quedará bloqueada para edición. Esta acción no se puede deshacer.', labelConfirmar: 'Sí, cancelar', variante: 'danger' });
        if (!ok) return;
        updated = await otcService.cancelar(currentOtcId, { fecha_cierre: todayYmd() });
        toast.success('OTC cancelada correctamente.');
      }

      if (onSave) {
        await onSave(updated);
      }
      if (onCancel) {
        onCancel();
        return;
      }
      await loadDetail(currentOtcId);
    } catch (e) {
      const data = e?.response?.data;
      const lecturaErr = data?.odometro_realizado;
      toast.error(
        data?.detail
        || (Array.isArray(lecturaErr) ? lecturaErr[0] : lecturaErr)
        || 'No se pudo cambiar el estado.'
      );
    }
  };

  const addTask = () => {
    if (!newTask.tarea_catalogo) {
      toast.warning('Seleccioná una tarea del catálogo.');
      return;
    }
    const cat = tareasCatalogo.find(t => String(t.id) === String(newTask.tarea_catalogo));
    if (!cat) { toast.warning('Tarea no encontrada.'); return; }
    const yaExiste = tareas.some(t => t.titulo === cat.nombre);
    if (yaExiste) {
      toast.warning(`La tarea "${cat.nombre}" ya está agregada en esta OTC.`);
      return;
    }
    setTareas(prev => [...prev, {
      id: `_temp_${Date.now()}_${Math.random()}`,
      catalogo_tarea_id: cat.id,
      orden_item: prev.length + 1,
      titulo: cat.nombre,
      descripcion: cat.descripcion || '',
      realizado: false,
      horas_hombre: null,
      observaciones: '',
    }]);
    setNewTask({ tarea_catalogo: '' });
    setTaskSearch('');
    triggerAutoSaveNow();
  };

  const addTaskFromCatalog = (taskId) => {
    if (!taskId) return;
    const cat = tareasCatalogo.find(t => String(t.id) === String(taskId));
    if (!cat) return;
    const yaExiste = tareas.some(t => t.titulo === cat.nombre);
    if (yaExiste) {
      toast.warning(`La tarea "${cat.nombre}" ya está agregada en esta OTC.`);
      return;
    }
    setTareas(prev => [...prev, {
      id: `_temp_${Date.now()}_${Math.random()}`,
      catalogo_tarea_id: cat.id,
      orden_item: prev.length + 1,
      titulo: cat.nombre,
      descripcion: cat.descripcion || '',
      realizado: false,
      horas_hombre: null,
      observaciones: '',
    }]);
    setNewTask({ tarea_catalogo: '' });
    setTaskSearch('');
    triggerAutoSaveNow();
  };



  const handlePrintBlank = async () => {
    const fmtDate = (s) => {
      if (!s) return '-';
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) { const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; }
      return s;
    };
    const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const numero        = esc(otc?.numero_ot || '');
    const interno       = esc(equipoSeleccionado?.interno || '-');
    const marcaModelo   = esc(`${equipoSeleccionado?.marca_nombre || '-'} / ${equipoSeleccionado?.modelo_nombre || '-'}`);
    const descripEq     = esc(equipoSeleccionado?.descripcion || '-');
    const ubicacion     = esc(equipoSeleccionado?.ubicacion_nombre || '-');
    const obra          = esc(equipoSeleccionado?.obra_nombre || '-');
    const odometro      = esc(String(equipoSeleccionado?.odometro_actual ?? equipoSeleccionado?.odometro ?? '-'));
    const contadorNom   = esc(equipoSeleccionado?.contador_nombre || '');
    const familiaTipo   = esc(`${equipoSeleccionado?.familia_nombre || '-'} / ${equipoSeleccionado?.tipo_nombre || '-'}`);
    const titulo        = esc(form.titulo || '-');
    const descProblema  = esc(form.descripcion || '');
    const observaciones = esc(form.observaciones || '');
    const fechaApertura = fmtDate(form.fecha_apertura);
    const fechaCierre   = fmtDate(form.fecha_cierre_planificada);
    const estadoEquipo  = esc(equipoSeleccionado?.estado_nombre || '-');
    const sucursal      = esc(equipoSeleccionado?.sucursal_nombre || '-');
    const equipoNombre  = esc(equipoLabel(equipoSeleccionado || {}) || '-');
    const rawLogo       = resolveCompanyLogoUrl(logoEmpresa);
    const logoSrc       = esc(rawLogo);
    const logoMarkup    = logoSrc
      ? `<img src="${logoSrc}" alt="Logo empresa" style="max-height:40px;max-width:120px;object-fit:contain" />`
      : '';

    const tick = (v) => (v ? 'X' : '&nbsp;');
    const cellOrBlank = (v) => {
      const s = v == null ? '' : String(v).trim();
      return s ? esc(s) : '&nbsp;';
    };
    const isClosed = form.estado === 'cerrada';

    const tareasRows = tareas.map((t, idx) =>
      `<tr class="dr"><td class="c">${idx + 1}</td><td>${esc(t.titulo || '')}</td><td class="c">${tick(t.realizado)}</td><td class="c">${cellOrBlank(t.horas_hombre)}</td></tr>`
    ).join('');
    const blankTareas = (isClosed ? '' : Array.from({ length: Math.max(4, 6 - tareas.length) }).map(() =>
      '<tr><td class="c" style="height:22px">&nbsp;</td><td>&nbsp;</td><td class="c">&nbsp;</td><td class="c">&nbsp;</td></tr>'
    ).join(''));

    const repRows = repuestos.map((r) => {
      const usado = (r.cantidad_usada != null && String(r.cantidad_usada).trim() !== '' && Number(r.cantidad_usada) > 0);
      const repuestoTexto = [r.repuesto_codigo, r.repuesto_marca, r.repuesto_descripcion].filter(Boolean).join(' - ');
      return `<tr class="dr"><td>${esc(repuestoTexto)}</td><td class="c">${tick(usado)}</td><td class="c">${esc(String(r.cantidad_planificada ?? ''))}</td><td class="c">${cellOrBlank(r.repuesto_unidad_medida)}</td><td class="c">${cellOrBlank(r.cantidad_usada)}</td><td class="c">${cellOrBlank(r.costo_unitario)}</td></tr>`;
    }).join('');
    const blankRep = (isClosed ? '' : Array.from({ length: Math.max(3, 5 - repuestos.length) }).map(() =>
      '<tr><td style="height:22px">&nbsp;</td><td class="c">&nbsp;</td><td class="c">&nbsp;</td><td class="c">&nbsp;</td><td class="c">&nbsp;</td><td class="c">&nbsp;</td></tr>'
    ).join(''));

    const repuestoIds = Array.from(new Set(
      (repuestos || [])
        .map((r) => (typeof r.repuesto === 'object' ? r.repuesto.id : r.repuesto))
        .filter(Boolean)
        .map((id) => Number(id))
    ));
    const equivalenciasPrintCache = await fetchEquivalenciasByRepuestoIds(repuestoIds, equipoSeleccionado?.obra);
    const equivalenciasRows = Object.entries(equivalenciasPrintCache).map(([repId, equivalentes]) => {
      if (!Array.isArray(equivalentes) || equivalentes.length === 0) return '';
      const original = repuestos.find((r) => {
        const rid = typeof r.repuesto === 'object' ? r.repuesto.id : r.repuesto;
        return String(rid) === String(repId);
      });
      const originalText = esc([original?.repuesto_codigo, original?.repuesto_marca, original?.repuesto_descripcion].filter(Boolean).join(' - ') || '-');
      return equivalentes.map((eq, idx) => {
        const eqText = esc([eq.codigo, eq.marca_nombre, eq.descripcion].filter(Boolean).join(' - ') || '-');
        const stockObra = esc(String(eq.stock_en_obra ?? '-'));
        if (idx === 0) {
          return `<tr><td rowspan="${equivalentes.length}" style="vertical-align:top;white-space:nowrap">${originalText}</td><td style="white-space:nowrap">${eqText}</td><td class="c">${stockObra}</td><td class="c">&nbsp;</td></tr>`;
        }
        return `<tr><td style="white-space:nowrap">${eqText}</td><td class="c">${stockObra}</td><td class="c">&nbsp;</td></tr>`;
      }).join('');
    }).join('');
    const eqThSt = `border:1px solid ${theme.colors.printBorder};padding:4px 6px;font-weight:700;background:${theme.colors.printBg}`;
    const equivalenciasSectionHtml = equivalenciasRows
      ? `<div class="section"><div class="section-title">Equivalencias de Repuestos</div><table style="font-size:10px"><thead><tr><th style="${eqThSt};text-align:left">Repuesto Original</th><th style="${eqThSt};text-align:left">Equivalente</th><th style="${eqThSt};width:90px;text-align:center">Stock Obra</th><th style="${eqThSt};width:70px;text-align:center">Usado</th></tr></thead><tbody>${equivalenciasRows}</tbody></table></div>`
      : '';

    const mecRows = otcMecanicos.map((m) => {
      const horas = Number(m.horas_trabajadas) || 0;
      const costoH = Number(m.costo_hora) || 0;
      const subtotal = horas * costoH;
      return `<tr class="dr"><td>${esc(m.mecanico_nombre || '')}</td><td class="c">${cellOrBlank(m.horas_trabajadas)}</td><td class="c">${cellOrBlank(m.costo_hora)}</td><td class="c">${subtotal > 0 ? subtotal.toFixed(0) : '&nbsp;'}</td></tr>`;
    }).join('');
    const blankMec = (isClosed ? '' : Array.from({ length: Math.max(2, 4 - otcMecanicos.length) }).map(() =>
      '<tr><td style="height:22px">&nbsp;</td><td class="c">&nbsp;</td><td class="c">&nbsp;</td><td class="c">&nbsp;</td></tr>'
    ).join(''));

    const gasRows = gastosExtra.map((g) => {
      const total = (Number(g.cantidad) || 0) * (Number(g.precio_unitario) || 0);
      return `<tr class="dr"><td>${esc(g.concepto || '')}</td><td class="c">${esc(String(g.precio_unitario ?? ''))}</td><td class="c">${esc(String(g.cantidad ?? ''))}</td><td class="c">${total > 0 ? total.toFixed(0) : '&nbsp;'}</td></tr>`;
    }).join('');
    const blankGas = (isClosed ? '' : Array.from({ length: Math.max(2, 4 - gastosExtra.length) }).map(() =>
      '<tr><td style="height:22px">&nbsp;</td><td class="c">&nbsp;</td><td class="c">&nbsp;</td><td class="c">&nbsp;</td></tr>'
    ).join(''));

    const thSt = `border:1px solid ${theme.colors.printBorder};padding:4px 6px;font-weight:700;text-align:center;background:${theme.colors.printBg}`;
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>OTC ${numero}</title>
<style>
  @page{size:A4 portrait;margin:0.24in 0.12in 0.24in 0.52in}
  *{box-sizing:border-box}
  body{margin:0;font-family:Arial,Helvetica,sans-serif;font-size:11px;color:${theme.colors.printText};background:${theme.colors.printBg}}
  table{border-collapse:collapse;width:100%}
  th{background:${theme.colors.printBg}}
  .section{border:1.5px solid ${theme.colors.printRowObsText};border-radius:6px;padding:10px 12px;margin-bottom:10px;break-inside:avoid;page-break-inside:avoid}
  .section-title{font-size:12px;font-weight:700;margin-bottom:6px;text-align:center}
  .info-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:4px 12px;margin-bottom:6px}
  .info-item label{font-size:9px;font-weight:700;display:block;color:${theme.colors.printRowObsText};text-transform:uppercase}
  .info-item span{display:block;font-size:11px}
  .fillbox{border:1px solid ${theme.colors.printBorderDashed};border-radius:3px;min-height:18px;padding:1px 5px;display:inline-block;min-width:80px}
  .drow{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px}
  .dbox{border:1px solid ${theme.colors.printBorderDashed};border-radius:4px;padding:7px;min-height:52px;font-size:11px}
  .dbox label{font-size:9px;font-weight:700;text-transform:uppercase;color:${theme.colors.printRowObsText};display:block;margin-bottom:4px}
  td{border:1px solid ${theme.colors.printBorder};padding:3px 4px}
  .c{text-align:center}
  .firma-box{border:1px solid ${theme.colors.printRowObsText};border-radius:8px;padding:8px;height:90px;display:flex;flex-direction:column;justify-content:flex-end}
  .firma-line{border-top:1px solid ${theme.colors.printRowObsText};margin-top:12px;padding-top:5px;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;color:${theme.colors.printRowObsText}}
  #firmas{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
</style></head>
<body>

  <!-- ENCABEZADO -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6px;border-bottom:2px solid ${theme.colors.printBorder};padding-bottom:6px">
    <div>
      <div style="font-size:14px;font-weight:800">ORDEN DE TRABAJO CORRECTIVA</div>
      <div style="font-size:12px;font-weight:700;margin-top:2px">N° OT: ${numero}</div>
      <div style="font-size:11px;margin-top:2px">Título: <strong>${titulo}</strong></div>
    </div>
    <div style="display:flex;align-items:center;gap:12px">
      ${logoMarkup}
    </div>
  </div>

  <!-- INFO EQUIPO -->
  <div class="info-grid" style="margin-bottom:8px">
    <div class="info-item"><label>INTERNO</label><span>${interno}</span></div>
    <div class="info-item"><label>MARCA / MODELO</label><span>${marcaModelo}</span></div>
    <div class="info-item"><label>DESCRIPCIÓN</label><span>${descripEq}</span></div>
    <div class="info-item"><label>FAMILIA / TIPO</label><span>${familiaTipo}</span></div>
    <div class="info-item"><label>UBICACIÓN</label><span>${ubicacion}</span></div>
    <div class="info-item"><label>OBRA</label><span>${obra}</span></div>
    <div class="info-item"><label>HORÓMETRO / ODÓMETRO</label><span>${odometro} ${contadorNom}</span></div>
    <div class="info-item"><label>ESTADO</label><span>${estadoEquipo}</span></div>
    <div class="info-item"><label>SUCURSAL</label><span>${sucursal}</span></div>
  </div>

  <!-- EQUIPO Y TÍTULO -->
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px 12px;margin-bottom:8px">
    <div class="info-item"><label>EQUIPO</label><span>${equipoNombre}</span></div>
    <div class="info-item"><label>TÍTULO / MOTIVO</label><span>${titulo}</span></div>
  </div>

  <!-- FECHAS -->
  <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:4px 12px;margin-bottom:10px;border:1px solid ${theme.colors.printBorderDashed};border-radius:4px;padding:6px">
    <div class="info-item"><label>FECHA APERTURA</label><span>${fechaApertura}</span></div>
    <div class="info-item"><label>FECHA CIERRE PLANIF.</label><span>${fechaCierre}</span></div>
    <div class="info-item"><label>FECHA CIERRE REAL</label><span class="fillbox">${form.fecha_cierre ? fmtDate(form.fecha_cierre) : '&nbsp;'}</span></div>
    <div class="info-item"><label>LECTURA CIERRE (HS / KM)</label><span class="fillbox">${cellOrBlank(otc?.odometro_realizado)}</span></div>
  </div>

  <!-- DESCRIPCIÓN Y OBSERVACIONES -->
  <div class="drow">
    <div class="dbox"><label>Descripción del Problema</label>${descProblema}</div>
    <div class="dbox"><label>Observaciones</label>${observaciones}</div>
  </div>

  <!-- TAREAS -->
  <div class="section">
    <div class="section-title">Tareas de la OTC</div>
    <table style="font-size:10px">
      <thead><tr>
        <th style="${thSt};width:32px">#</th>
        <th style="${thSt};text-align:left">Tarea</th>
        <th style="${thSt};width:90px">Realizada (S/N)</th>
        <th style="${thSt};width:72px">Horas</th>
      </tr></thead>
      <tbody>${tareasRows}${blankTareas}</tbody>
    </table>
  </div>

  <!-- REPUESTOS -->
  <div class="section">
    <div class="section-title">Repuestos</div>
    <table style="font-size:10px">
      <thead><tr>
        <th style="${thSt};text-align:left">Repuesto</th>
        <th style="${thSt};width:80px">Utilizado (S/N)</th>
        <th style="${thSt};width:86px">Cant. Planif.</th>
        <th style="${thSt};width:70px">Unidad</th>
        <th style="${thSt};width:86px">Cant. Usada</th>
        <th style="${thSt};width:86px">Costo Unit.</th>
      </tr></thead>
      <tbody>${repRows}${blankRep}</tbody>
    </table>
  </div>

  ${equivalenciasSectionHtml}

  <!-- EJECUCIÓN -->
  <div class="section">
    <div class="section-title">Ejecución del Mantenimiento</div>
    <div style="margin-bottom:8px">
      <div style="font-weight:700;text-align:center;margin-bottom:4px;font-size:11px">Mecánicos y Mano de Obra</div>
      <table style="font-size:10px">
        <thead><tr>
          <th style="${thSt};text-align:left">Mecánico</th>
          <th style="${thSt};width:72px">Horas</th>
          <th style="${thSt};width:100px">Costo / Hora</th>
          <th style="${thSt};width:100px">Subtotal</th>
        </tr></thead>
        <tbody>${mecRows}${blankMec}</tbody>
      </table>
    </div>
    <div>
      <div style="font-weight:700;text-align:center;margin-bottom:4px;font-size:11px">Gastos Extra</div>
      <table style="font-size:10px">
        <thead><tr>
          <th style="${thSt};text-align:left">Concepto</th>
          <th style="${thSt};width:100px">Precio Unit.</th>
          <th style="${thSt};width:80px">Cantidad</th>
          <th style="${thSt};width:100px">Total</th>
        </tr></thead>
        <tbody>${gasRows}${blankGas}</tbody>
      </table>
    </div>
  </div>

  ${(isClosed || totalGeneral > 0) ? `
  <!-- RESUMEN DE COSTOS -->
  <div class="section">
    <div class="section-title">Resumen de Costos</div>
    <table style="font-size:11px;width:100%">
      <tr><td style="padding:3px 8px;border:0;width:60%">Repuestos</td><td style="padding:3px 8px;border:0;text-align:right">${fmtOtc(totalRepuestos)}</td></tr>
      <tr><td style="padding:3px 8px;border:0">Mano de obra</td><td style="padding:3px 8px;border:0;text-align:right">${fmtOtc(totalManoObra)}</td></tr>
      <tr><td style="padding:3px 8px;border:0">Gastos extra</td><td style="padding:3px 8px;border:0;text-align:right">${fmtOtc(totalGastosExtra)}</td></tr>
      <tr><td style="padding:6px 8px;border:0;border-top:1.5px solid ${theme.colors.printBorder};font-weight:700">TOTAL GENERAL OTC</td><td style="padding:6px 8px;border:0;border-top:1.5px solid ${theme.colors.printBorder};text-align:right;font-weight:700">${fmtOtc(totalGeneral)}</td></tr>
    </table>
  </div>
  ` : ''}

  <div id="firma-spacer" style="height:0"></div>
  <div id="firmas">
    <div class="firma-box"><div class="firma-line">Chofer / Operador</div></div>
    <div class="firma-box"><div class="firma-line">Realizado Por</div></div>
    <div class="firma-box"><div class="firma-line">Revisado Por</div></div>
  </div>
<script>
window.onload = function () {
  var pageH = 11.2129 * 96;
  var spacer = document.getElementById('firma-spacer');
  if (spacer) {
    var printExtra = 0;
    var secs = Array.prototype.slice.call(document.querySelectorAll('.section'));
    secs.sort(function(a, b) { return a.getBoundingClientRect().top - b.getBoundingClientRect().top; });
    secs.forEach(function(el) {
      var top = el.getBoundingClientRect().top + window.scrollY + printExtra;
      var h   = el.offsetHeight;
      var pos = top % pageH;
      if (pos > 0 && pos + h > pageH) {
        printExtra += pageH - pos;
      }
    });
    var firmasEl = document.getElementById('firmas');
    var firmasH = firmasEl ? firmasEl.offsetHeight : 0;
    var screenH   = document.body.scrollHeight;
    var contentH  = screenH - firmasH;
    var adjustedH = contentH + printExtra;
    var pages     = Math.ceil((adjustedH + firmasH + 1) / pageH);
    var gap       = pages * pageH - adjustedH - firmasH - 4;
    if (gap < 0) { pages += 1; gap = pages * pageH - adjustedH - firmasH - 4; }
    spacer.style.height = gap + 'px';
    void spacer.offsetHeight;
  }
  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      window.print();
    });
  });
};
</script>
</body></html>`;

    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;top:0;left:-850px;width:732px;height:1080px;border:0;overflow:hidden';
    document.body.appendChild(frame);
    const prevTitle = document.title;
    document.title = `OTC ${otc?.numero_ot || ''}`;
    const removeFrame = () => {
      if (frame.parentNode) frame.parentNode.removeChild(frame);
      document.title = prevTitle;
    };
    frame.contentDocument.open();
    frame.contentDocument.write(html);
    frame.contentDocument.close();
    frame.contentWindow.addEventListener('afterprint', removeFrame);
    setTimeout(removeFrame, 120000);
  };

  const deleteTask = async (id) => {
    const ok = await confirmar({ titulo: '¿Eliminar tarea?', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return;
    if (typeof id === 'string' && id.startsWith('_temp_')) {
      setTareas(prev => prev.filter(t => t.id !== id));
      triggerAutoSaveNow();
      return;
    }
    try {
      await otcService.eliminarTarea(id);
      setTareas(prev => prev.filter(t => t.id !== id));
      triggerAutoSaveNow();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar la tarea.');
    }
  };

  const addRepuesto = () => {
    if (!newRep.repuesto) {
      toast.warning('Seleccioná un repuesto.');
      return;
    }
    const repId = Number(newRep.repuesto);
    const cantAdd = Number(newRep.cantidad_planificada) || 1;
    const existente = repuestos.find(r => {
      const rRepId = typeof r.repuesto === 'object' ? r.repuesto.id : r.repuesto;
      return Number(rRepId) === repId && (r.tarea || null) === (newRep.tarea || null);
    });
    if (existente) {
      setRepuestos(prev => prev.map(r => r.id === existente.id
        ? { ...r, cantidad_planificada: (Number(r.cantidad_planificada) || 0) + cantAdd }
        : r
      ));
      toast.info(`El repuesto ya existía en la lista. Se incrementó la cantidad planificada en ${cantAdd}.`);
      setNewRep({ tarea: '', repuesto: '', cantidad_planificada: 1, cantidad_usada: '', costo_unitario: '' });
      triggerAutoSaveNow();
      return;
    }
    const repCat = repuestosCatalogo.find(r => String(r.id) === String(newRep.repuesto));
    setRepuestos(prev => [...prev, {
      id: `_temp_${Date.now()}_${Math.random()}`,
      tarea: newRep.tarea || null,
      repuesto: repId,
      repuesto_codigo: repCat?.codigo || '',
      repuesto_descripcion: repCat?.descripcion || '',
      repuesto_unidad_medida: repCat?.unidad_medida_abreviatura || repCat?.unidad_medida_nombre || '',
      cantidad_planificada: cantAdd,
      cantidad_usada: newRep.cantidad_usada === '' ? null : Number(newRep.cantidad_usada),
      costo_unitario: newRep.costo_unitario === '' ? null : newRep.costo_unitario,
      observaciones: '',
    }]);
    setNewRep({ tarea: '', repuesto: '', cantidad_planificada: 1, cantidad_usada: '', costo_unitario: '' });
    triggerAutoSaveNow();
  };

  const addMecanico = () => {
    if (!newMec.mecanico) {
      toast.warning('Seleccioná un mecánico.');
      return;
    }
    if (newMec.horas_trabajadas !== '' && Number(newMec.horas_trabajadas) <= 0) {
      toast.warning('Las horas trabajadas deben ser mayores a 0.');
      return;
    }
    const mecId = Number(newMec.mecanico);
    const yaExiste = otcMecanicos.some(m => {
      const mId = typeof m.mecanico === 'object' ? m.mecanico.id : m.mecanico;
      return Number(mId) === mecId;
    });
    if (yaExiste) {
      toast.warning('Este mecánico ya está agregado en la OTC. Modificá sus horas en la tabla.');
      return;
    }
    const mecCat = mecanicos.find(m => String(m.id) === String(newMec.mecanico));
    setOtcMecanicos(prev => [...prev, {
      id: `_temp_${Date.now()}_${Math.random()}`,
      mecanico: mecId,
      mecanico_nombre: mecCat?.nombre || '',
      horas_trabajadas: newMec.horas_trabajadas === '' ? null : newMec.horas_trabajadas,
      costo_hora: newMec.costo_hora === '' ? null : newMec.costo_hora,
      observaciones: '',
    }]);
    setNewMec({ mecanico: '', horas_trabajadas: '', costo_hora: '' });
    triggerAutoSaveNow();
  };

  const addGastoExtra = () => {
    const concepto = (newGasto.concepto || '').trim();
    if (!concepto) {
      toast.warning('Ingresá el concepto del gasto.');
      return;
    }
    const cantidad = Number(newGasto.cantidad) || 0;
    const precio = Number(newGasto.precio_unitario) || 0;
    if (cantidad <= 0 || !Number.isInteger(cantidad) || precio < 0) {
      toast.warning('La cantidad debe ser un número entero mayor a 0.');
      return;
    }
    setGastosExtra(prev => ([
      ...prev,
      {
        id: `_temp_${Date.now()}_${Math.random()}`,
        concepto,
        cantidad,
        precio_unitario: precio,
      }
    ]));
    setNewGasto({ concepto: '', precio_unitario: '', cantidad: 1 });
    triggerAutoSaveNow();
  };

  const deleteGastoExtra = async (id) => {
    const ok = await confirmar({ titulo: '¿Eliminar gasto extra?', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return;
    if (typeof id === 'string' && id.startsWith('_temp_')) {
      setGastosExtra(prev => prev.filter(g => g.id !== id));
      triggerAutoSaveNow();
      return;
    }
    try {
      await otcService.eliminarGastoExtra(id);
      setGastosExtra(prev => prev.filter(g => g.id !== id));
      triggerAutoSaveNow();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar el gasto extra.');
    }
  };

  const deleteMecanico = async (id) => {
    const ok = await confirmar({ titulo: '¿Eliminar mecánico de la OTC?', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return;
    if (typeof id === 'string' && id.startsWith('_temp_')) {
      setOtcMecanicos(prev => prev.filter(m => m.id !== id));
      triggerAutoSaveNow();
      return;
    }
    try {
      await otcService.eliminarMecanico(id);
      setOtcMecanicos(prev => prev.filter(m => m.id !== id));
      triggerAutoSaveNow();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar el mecánico.');
    }
  };

  const handleSelectEquivalente = (equiv) => {
    if (eqIdx === null) return;
    const original = repuestos[eqIdx];
    const originalLabel = [original?.repuesto_codigo, original?.repuesto_marca, original?.repuesto_descripcion].filter(Boolean).join(' - ');
    const equivLabel = [equiv.codigo, equiv.marca_nombre, equiv.descripcion].filter(Boolean).join(' - ');
    const msg = `Reemplazo por equivalencia: ${originalLabel} -> ${equivLabel}.`;
    setRepuestos(prev => prev.map((r, idx) => {
      if (idx !== eqIdx) return r;
      const obsActual = String(r.observaciones || '').trim();
      const nuevaObs = obsActual && !obsActual.includes(msg) ? `${obsActual}\n${msg}` : (obsActual || msg);
      return {
        ...r,
        repuesto_codigo: equiv.codigo,
        repuesto_descripcion: equiv.descripcion,
        repuesto_marca: equiv.marca_nombre,
        repuesto: equiv.id,
        precio_unitario: equiv.precio_unitario ?? null,
        observaciones: nuevaObs,
      };
    }));
    setShowEqModal(false);
    setEqIdx(null);
    triggerAutoSaveNow();
  };

  const handleOpenEquivalencias = (idx, repuestoId) => {
    if (!repuestoId) return;
    setEqIdx(idx);
    setShowEqModal(true);
  };

  const deleteRepuesto = async (id) => {
    const ok = await confirmar({ titulo: '¿Eliminar repuesto de la OTC?', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return;
    if (typeof id === 'string' && id.startsWith('_temp_')) {
      setRepuestos(prev => prev.filter(r => r.id !== id));
      triggerAutoSaveNow();
      return;
    }
    try {
      await otcService.eliminarRepuesto(id);
      setRepuestos(prev => prev.filter(r => r.id !== id));
      triggerAutoSaveNow();
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.requiere_confirmacion) return;
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar el repuesto.');
    }
  };

  const deleteRepuestoAdicional = async (id) => {
    const ok = await confirmar({ titulo: '¿Eliminar repuesto adicional de la OTC?', labelConfirmar: 'Sí, eliminar', variante: 'danger' });
    if (!ok) return;
    if (typeof id === 'string' && id.startsWith('_temp_')) {
      setRepuestosAdicionales(prev => prev.filter(r => r.id !== id));
      triggerAutoSaveNow();
      return;
    }
    try {
      await otcService.eliminarRepuesto(id);
      setRepuestosAdicionales(prev => prev.filter(r => r.id !== id));
      triggerAutoSaveNow();
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.requiere_confirmacion) return;
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar el repuesto adicional.');
    }
  };

  // Selected equipo info
  const equipoSeleccionado = useMemo(() => {
    if (!form.equipo) return null;
    return equipos.find(e => String(e.id) === String(form.equipo)) || null;
  }, [form.equipo, equipos]);

  // Pre-seleccionar el estado actual del equipo cuando cambia el equipo seleccionado
  useEffect(() => {
    if (equipoSeleccionado?.estado) {
      setEstadoEquipoSeleccionado(String(equipoSeleccionado.estado));
    } else {
      setEstadoEquipoSeleccionado('');
    }
  }, [equipoSeleccionado?.estado]);

  const getOtcOriginalByRepId = useCallback((repId) => {
    const original = (repuestos || []).find((r) => {
      const rRepId = typeof r.repuesto === 'object' ? r.repuesto.id : r.repuesto;
      return String(rRepId) === String(repId);
    });

    if (!original) return null;
    return {
      codigo: original.repuesto_codigo || '-',
      descripcion: original.repuesto_descripcion || '-',
    };
  }, [repuestos]);

  useEffect(() => {
    let mounted = true;

    const cargarEquivalencias = async () => {
      const repuestoIds = Array.from(new Set(
        (repuestos || [])
          .map((r) => (typeof r.repuesto === 'object' ? r.repuesto.id : r.repuesto))
          .filter(Boolean)
          .map((id) => Number(id))
      ));

      const cache = await fetchEquivalenciasByRepuestoIds(repuestoIds, equipoSeleccionado?.obra);
      if (mounted) {
        setEquivalenciasCache(cache);
      }
    };

    if (!equipoSeleccionado?.obra || !(repuestos || []).length) {
      setEquivalenciasCache({});
      return () => { mounted = false; };
    }

    cargarEquivalencias();
    return () => { mounted = false; };
  }, [repuestos, equipoSeleccionado?.obra, fetchEquivalenciasByRepuestoIds]);

  if (loading) {
    return <div>Cargando OTC...</div>;
  }

  return (
    <WorkspacePage>
      <WorkspaceHeader>
        <BackButton type="button" onClick={salirConAviso}>
          <i className="bi bi-arrow-left" />
          <ButtonLabel>Volver</ButtonLabel>
        </BackButton>
        <TitleArea>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Title>{currentOtcId ? 'Orden de Trabajo Correctiva' : 'Nueva Orden de Trabajo Correctiva'}</Title>
            <HelpToggleBtn
              type="button"
              $active={helpOpen}
              onClick={() => setHelpOpen(v => !v)}
            >
              <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
              Ayuda
            </HelpToggleBtn>
          </div>
          <Subtitle>
            {currentOtcId
              ? `N° ${otc?.numero_ot || '—'}${equipoSeleccionado ? ` · ${equipoLabel(equipoSeleccionado)}` : (otc?.equipo_interno ? ` · ${otc.equipo_interno}` : '')}`
              : 'Registrá una nueva orden de trabajo correctiva'}
          </Subtitle>
        </TitleArea>
        {currentOtcId && form.estado ? (
          <StatusPill $tone={form.estado === 'cerrada' ? 'success' : form.estado === 'cancelada' ? 'danger' : 'primary'}>
            {estadoLabel[form.estado] || form.estado}
          </StatusPill>
        ) : null}
        <HeaderActions>
          <Button onClick={handlePrintBlank} disabled={!currentOtcId}>
            <i className="bi bi-printer" /> <ButtonLabel>Imprimir</ButtonLabel>
          </Button>
          {currentOtcId && !locked ? (
            <>
              <AutoSaveIndicator
                $status={autoSaveStatus === 'idle' && autoSavedAt ? 'saved-persisted' : autoSaveStatus}
                title={autoSaveStatus === 'error' ? autoSaveErrorMsg : undefined}
              >
                {autoSaveStatus === 'saving' && <><i className="bi bi-arrow-repeat" /> Guardando...</>}
                {autoSaveStatus === 'saved' && <><i className="bi bi-check-circle-fill" /> Guardado {autoSavedAt?.toLocaleTimeString('es-AR', { timeZone: ZONA_ARGENTINA, hour: '2-digit', minute: '2-digit', second: '2-digit' })}</>}
                {autoSaveStatus === 'error' && <><i className="bi bi-exclamation-triangle-fill" /> {autoSaveErrorMsg || 'Error al guardar'}</>}
                {autoSaveStatus === 'idle' && autoSavedAt && <><i className="bi bi-check-circle" /> Guardado {autoSavedAt?.toLocaleTimeString('es-AR', { timeZone: ZONA_ARGENTINA, hour: '2-digit', minute: '2-digit', second: '2-digit' })}</>}
              </AutoSaveIndicator>
              <Button
                onClick={() => { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; handleSaveHeader({ silent: false }); }}
                disabled={saving}
                title="Guardar ahora"
              >
                <i className="bi bi-save2" /> <ButtonLabel>Guardar</ButtonLabel>
              </Button>
            </>
          ) : !currentOtcId ? (
            <Button $success onClick={() => handleSaveHeader({ silent: false })} disabled={saving}>
              {saving ? 'Creando...' : 'Crear OTC'}
            </Button>
          ) : null}
        </HeaderActions>
        <WorkspaceActionsMenu
          actions={[
            { key: 'volver', label: 'Volver', icon: 'bi bi-arrow-left', onClick: salirConAviso },
            { key: 'imprimir', label: 'Imprimir', icon: 'bi bi-printer', disabled: !currentOtcId, onClick: handlePrintBlank },
            currentOtcId && !locked
              ? { key: 'guardar', label: 'Guardar', icon: 'bi bi-save2', tone: 'success', disabled: saving, onClick: () => { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; handleSaveHeader({ silent: false }); } }
              : (!currentOtcId
                ? { key: 'crear', label: saving ? 'Creando...' : 'Crear OTC', icon: 'bi bi-save2', tone: 'success', disabled: saving, onClick: () => handleSaveHeader({ silent: false }) }
                : null),
          ]}
        />
      </WorkspaceHeader>

      {helpOpen && (
        <div style={{ margin: '0 0 10px' }}>
          <HelpPanel
            prereqs={[
              'Tener cargados los mecánicos, los estados de reparación y el catálogo de repuestos.',
            ]}
            tips={[
              'La orden se guarda sola mientras trabajás: no hace falta apretar Guardar en cada cambio.',
              'Cargá las tareas realizadas y las horas de cada mecánico: de ahí sale el costo de mano de obra.',
              'Los repuestos que marques como usados descuentan stock del almacén.',
              'Si el equipo quedó parado, registrá la detención para que impacte en la disponibilidad.',
            ]}
            nextSteps={[
              'Cerrar la orden cuando el trabajo esté terminado y verificado.',
              'Una vez cerrada, la orden alimenta los informes de costos, fallas y tiempos de reparación.',
            ]}
            inlineOpen
          />
        </div>
      )}

      <FloatingAutoSave status={autoSaveStatus} savedAt={autoSavedAt} errorMsg={autoSaveErrorMsg} />

      {error ? <NoticeBar $tone="danger">{error}</NoticeBar> : null}

      {locked ? (
        <NoticeBar>
          Esta OTC está {estadoLabel[form.estado] || form.estado}. La edición está bloqueada.{canDelete ? ' Podés eliminar ítems (tareas, repuestos, mecánicos, gastos) por tener permiso de eliminación.' : ''}
        </NoticeBar>
      ) : null}

      <WorkspaceBody $soloMain={activeTab !== 'detalles'}>
        <MainColumn $flowNatural={activeTab === 'detalles'} $soloMain={activeTab !== 'detalles'}>
          <Panel $flex $flowNatural={activeTab === 'detalles'}>
            <WorkspaceTabBar
              active={activeTab}
              onChange={setActiveTab}
              tabs={[
                { key: 'detalles', label: 'Detalles', icon: 'bi bi-info-circle' },
                { key: 'tareas', label: 'Tareas', icon: 'bi bi-list-check', count: tareas.length },
                { key: 'repuestos', label: 'Repuestos', icon: 'bi bi-box-seam', count: repuestos.length },
                { key: 'repuestosAlmacen', label: 'Repuestos de almacén', icon: 'bi bi-boxes', count: repuestosAdicionales.length },
                { key: 'manoObra', label: 'Mano de obra', icon: 'bi bi-person-gear', count: otcMecanicos.length },
                { key: 'gastos', label: 'Gastos extra', icon: 'bi bi-cash-coin', count: gastosExtra.length },
                { key: 'observaciones', label: 'Observaciones', icon: 'bi bi-card-text' },
                { key: 'adjuntos', label: 'Adjuntos', icon: 'bi bi-paperclip', count: adjuntos.length },
              ]}
            />

            <TabPanel clave="detalles" $active={activeTab === 'detalles'} $flowNatural>
              <TabBody $flowNatural>
                <DatosBody>
                  <SelectoresRow>
                    <InfoItem>
                      <label>Equipo *</label>
                      {modoMecanico ? (
                        <input
                          value={equipoSeleccionado ? equipoLabel(equipoSeleccionado) : (otc?.equipo_interno || '')}
                          disabled
                          readOnly
                        />
                      ) : (
                        <SearchableCombo
                          items={equipos}
                          value={form.equipo}
                          onChange={(v) => {
                            const equipoSel = equipos.find(e => String(e.id) === String(v));
                            const centroEquipo = getEquipoCentroCostoId(equipoSel);
                            setForm(f => ({ ...f, equipo: v, centro_costo: centroEquipo }));
                            triggerAutoSaveNow();
                          }}
                          onSearch={handleEquipoSearch}
                          disabled={locked}
                          placeholder="Buscar equipo por interno o descripción..."
                          getLabel={equipoLabel}
                          getFilterText={equipoFilter}
                          hasMore={equipoSearchHasMore}
                          searchError={equipoSearchError}
                          onLoadMore={handleEquipoLoadMore}
                          loadingMore={equipoLoadingMore}
                        />
                      )}
                    </InfoItem>
                    <InfoItem>
                      <label>Título / Motivo *</label>
                      <input value={form.titulo} disabled={locked} onChange={(e) => { setForm(v => ({ ...v, titulo: e.target.value })); scheduleAutoSaveText(); }} />
                    </InfoItem>
                    <InfoItem>
                      <label>Centro de costo</label>
                      <SearchableCombo
                        items={centrosCosto}
                        value={form.centro_costo}
                        onChange={(v) => { setForm(f => ({ ...f, centro_costo: v })); triggerAutoSaveNow(); }}
                        disabled={locked}
                        placeholder="Buscar centro de costo..."
                        getLabel={centroCostoLabel}
                        getFilterText={centroCostoFilter}
                      />
                    </InfoItem>
                    <InfoItem>
                      <label>Mecánico responsable</label>
                      {modoMecanico ? (
                        <input
                          value={mecanicos.find(m => String(m.id) === String(form.mecanico_responsable))?.nombre || form.mecanico_responsable || '—'}
                          disabled
                          readOnly
                        />
                      ) : (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <SearchableCombo
                              items={mecanicosConPortal}
                              value={form.mecanico_responsable}
                              onChange={(v) => {
                                setForm(f => ({ ...f, mecanico_responsable: v, fecha_asignacion_mecanico: '' }));
                                triggerAutoSaveNow();
                              }}
                              disabled={locked}
                              placeholder="Buscar mecánico con portal..."
                              getLabel={m => m.nombre}
                            />
                          </div>
                          {!locked && form.mecanico_responsable && (
                            <Button
                              type="button"
                              $success
                              title="Registrar fecha de asignación ahora"
                              onClick={() => { setForm(f => ({ ...f, fecha_asignacion_mecanico: todayYmd() })); triggerAutoSaveNow(); }}
                              style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                              {form.fecha_asignacion_mecanico ? '✓ Asignado' : 'Asignar'}
                            </Button>
                          )}
                          {form.fecha_asignacion_mecanico && puedeEditarFechasMantenimiento && !locked && (
                            <input
                              type="date"
                              value={form.fecha_asignacion_mecanico}
                              title="Editar fecha de asignación del mecánico"
                              onChange={(e) => { setForm(f => ({ ...f, fecha_asignacion_mecanico: e.target.value })); triggerAutoSaveNow(); }}
                              style={{ width: 130, flexShrink: 0 }}
                            />
                          )}
                        </div>
                      )}
                      {form.fecha_asignacion_mecanico && !(puedeEditarFechasMantenimiento && !locked) && (
                        <MutedText>Asignado el {form.fecha_asignacion_mecanico.split('-').reverse().join('/')}</MutedText>
                      )}
                    </InfoItem>
                    <InfoItem>
                      <label>Situación de la reparación</label>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <SearchableCombo
                            items={estadosReparacion}
                            value={form.estado_reparacion}
                            onChange={(v) => { setForm(f => ({ ...f, estado_reparacion: v })); triggerAutoSaveNow(); }}
                            disabled={locked}
                            placeholder="Buscar estado..."
                            getLabel={e => e.nombre}
                          />
                        </div>
                        {!locked && !modoMecanico && (
                          <ActionButton tabIndex={-1} title="Agregar estado de reparación" onClick={() => setShowNuevoEstadoReparacion(true)}>
                            <i className="bi bi-plus-lg" />
                          </ActionButton>
                        )}
                      </div>
                    </InfoItem>
                  </SelectoresRow>
                  {equipoSeleccionado ? (
                    <EquipoInfoGrid>
                      <InfoItem><label>Interno</label><span>{equipoSeleccionado.interno || '-'}</span></InfoItem>
                      <InfoItem><label>Marca / Modelo</label><span>{equipoSeleccionado.marca_nombre || '-'} / {equipoSeleccionado.modelo_nombre || '-'}</span></InfoItem>
                      <InfoItem><label>Descripción</label><span>{equipoSeleccionado.descripcion || '-'}</span></InfoItem>
                      <InfoItem><label>Familia / Tipo</label><span>{equipoSeleccionado.familia_nombre || '-'} / {equipoSeleccionado.tipo_nombre || '-'}</span></InfoItem>
                      <InfoItem><label>Ubicación</label><span>{equipoSeleccionado.ubicacion_nombre || '-'}</span></InfoItem>
                      <InfoItem><label>Obra</label><span>{equipoSeleccionado.obra_nombre || '-'}</span></InfoItem>
                      <InfoItem><label>Horómetro / Odómetro</label><span>{equipoSeleccionado.odometro_actual ?? equipoSeleccionado.odometro ?? '-'} {equipoSeleccionado.contador_nombre || ''}</span></InfoItem>
                      <InfoItem><label>Estado</label><span>{equipoSeleccionado.estado_nombre || '-'}</span></InfoItem>
                      <InfoItem><label>Sucursal</label><span>{equipoSeleccionado.sucursal_nombre || '-'}</span></InfoItem>
                    </EquipoInfoGrid>
                  ) : (
                    <MutedText style={{ textAlign: 'center', padding: '4px 0' }}>
                      Seleccioná un equipo para ver sus datos
                    </MutedText>
                  )}
                  <FechasRow>
                    <InfoItem>
                      <label>Fecha Apertura</label>
                      <input type="date" value={form.fecha_apertura} disabled={locked} onChange={(e) => { setForm(v => ({ ...v, fecha_apertura: e.target.value })); scheduleAutoSaveText(); }} />
                    </InfoItem>
                    <InfoItem>
                      <label>Cierre Planificado</label>
                      <input type="date" value={form.fecha_cierre_planificada} disabled={locked} onChange={(e) => { setForm(v => ({ ...v, fecha_cierre_planificada: e.target.value })); scheduleAutoSaveText(); }} />
                    </InfoItem>
                    {(locked || form.fecha_cierre) ? (
                      <InfoItem>
                        <label>Fecha Cierre</label>
                        <input type="date" value={form.fecha_cierre || ''} disabled />
                      </InfoItem>
                    ) : null}
                  </FechasRow>
                </DatosBody>
              </TabBody>
            </TabPanel>

            <TabPanel clave="tareas" $active={activeTab === 'tareas'}>
              <TabBody>
                <ZonaAltaRapida as={AddZone} onAgregar={addTask} disabled={locked}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'nowrap' }}>
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>Asignar tareas</label>
              <SearchSuggestField
                saltarAlSiguiente={false}
                value={taskSearch}
                onValueChange={(value) => {
                  setTaskSearch(value);
                  if (newTask.tarea_catalogo) {
                    setNewTask({ tarea_catalogo: '' });
                  }
                }}
                items={tareasDisponibles}
                selectedKey={newTask.tarea_catalogo}
                onSelectItem={(item) => {
                  setNewTask({ tarea_catalogo: String(item.id) });
                  setTaskSearch(item.nombre || '');
                }}
                onDoubleClickItem={(item) => addTaskFromCatalog(item.id)}
                getSearchText={(item) => `${item.nombre || ''} ${item.descripcion || ''} ${item?.componente?.nombre || ''}`}
                placeholder="Buscar por nombre o componente..."
                showAllOnFocus
                onSearch={buscarTareas}
                onLoadMore={cargarMasTareas}
                hasMore={tareasHasMore}
                loadingMore={tareasLoadingMore}
                searchError={tareasBusquedaError}
                renderItem={(item, { selected }) => (
                  <>
                    <div>
                      <strong>{item.nombre}</strong>
                      {item?.componente?.nombre ? ` (${item.componente.nombre})` : ''}
                    </div>
                    {selected ? <i className="bi bi-check2" /> : null}
                  </>
                )}
                emptyMessage="Sin opciones disponibles"
              />
            </div>
            <Button onClick={addTask} disabled={locked} style={{ flexShrink: 0 }}>Agregar</Button>
            {!modoMecanico && (
              <Button onClick={() => setShowNuevaTarea(true)} disabled={locked} style={{ flexShrink: 0 }}>Nuevo</Button>
            )}
          </div>
                </ZonaAltaRapida>
                <MutedText>Click para seleccionar. Doble click sobre una opción para agregarla directamente.</MutedText>

                <ScrollArea>
          <TableCard>
          <Table>
            <thead>
              <tr>
                <th style={{ width: 30 }}>#</th>
                <th>Tareas</th>
                <th style={{ width: 70 }}>Realizada</th>
                <th style={{ width: 80 }}>Horas</th>
                <th style={{ width: 42 }}></th>
              </tr>
            </thead>
            <tbody>
              {tareas.map((t, idx) => (
                <tr key={t.id}>
                  <td>{idx + 1}</td>
                  <td>{t.titulo || '-'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={!!t.realizado} disabled={locked} onChange={(e) => { const checked = e.target.checked; setTareas(prev => prev.map(x => x.id === t.id ? { ...x, realizado: checked } : x)); triggerAutoSaveNow(); }} />
                  </td>
                  <td>
                    <ThemedInput type="number" step="0.01" value={t.horas_hombre ?? ''} disabled={locked} onChange={(e) => { const val = e.target.value; setTareas(prev => prev.map(x => x.id === t.id ? { ...x, horas_hombre: val } : x)); scheduleAutoSaveText(); }} />
                  </td>
                  <td>
                    <DeleteIconButton className="row-actions" $variant="delete" title="Eliminar tarea" onClick={() => deleteTask(t.id)} disabled={locked && !canDelete}>
                      <i className="bi bi-trash" />
                    </DeleteIconButton>
                  </td>
                </tr>
              ))}
              {tareas.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center' }}><MutedText>Sin tareas cargadas.</MutedText></td>
                </tr>
              ) : null}
            </tbody>
          </Table>
          </TableCard>
                </ScrollArea>
              </TabBody>
            </TabPanel>

            <TabPanel clave="repuestos" $active={activeTab === 'repuestos'}>
              <TabBody>
                <ZonaAltaRapida as={AddZone} onAgregar={addRepuesto} disabled={locked}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 280px', minWidth: 260 }}>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>Código / Descripción</label>
              <SearchableCombo
                saltarAlSiguiente={false}
                items={repuestosCatalogo}
                value={newRep.repuesto}
                onChange={(v) => {
                  const repCat = repuestosCatalogo.find(r => String(r.id) === String(v));
                  setNewRep(r => ({
                    ...r,
                    repuesto: v,
                    costo_unitario: r.costo_unitario !== '' ? r.costo_unitario
                      : (repCat?.precio_unitario != null ? String(repCat.precio_unitario) : ''),
                  }));
                }}
                onSearch={handleRepuestoSearch}
                disabled={locked}
                placeholder="Buscar por código o descripción..."
                getLabel={repuestoLabel}
                getFilterText={(r) => `${r.codigo || ''} ${r.marca_nombre || ''} ${r.descripcion || ''}`}
                hasMore={repuestoSearchHasMore}
                searchError={repuestoSearchError}
                onLoadMore={handleRepuestoLoadMore}
                loadingMore={repuestoLoadingMore}
                // El desplegable se achica/agranda solo al ítem más largo (autoWidth
                // en SearchSuggestField) en vez de calcar el ancho del campo: así el
                // ícono, empujado al borde por el space-between del contenedor de
                // cada ítem, queda cerca del texto en vez de perderse a lo ancho de
                // un desplegable que antes calcaba el ancho (grande) del campo.
                autoWidth
                renderItem={(item) => (
                  <>
                    <span style={{ whiteSpace: 'nowrap' }}>{repuestoLabel(item)}</span>
                    {item.es_recomendado_equipo ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 10 }}>
                        {item.cantidad_recomendada_equipo ? (
                          <span style={{ fontSize: 11, opacity: 0.75 }}>{item.cantidad_recomendada_equipo}</span>
                        ) : null}
                        <i className="bi bi-gear" style={{ color: theme.colors.success }} title="Recomendado para este equipo" />
                      </span>
                    ) : null}
                  </>
                )}
              />
            </div>
            <div style={{ flex: '1 1 280px', minWidth: 220 }}>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>Asociar a (opcional)</label>
              <SearchableSelectField
                saltarAlSiguiente={false}
                options={tareas}
                value={newRep.tarea}
                onChange={(value) => setNewRep(v => ({ ...v, tarea: value }))}
                disabled={locked}
                placeholder="Sin tarea"
                getOptionValue={(option) => String(option.id)}
                getOptionLabel={(option) => option.titulo || `Tarea ${option.id}`}
                getOptionSearch={(option) => `${option.titulo || ''} ${option.descripcion || ''}`}
                showAllOnFocus
                size="sm"
              />
            </div>
            <div style={{ flex: '0 0 130px', minWidth: 110 }}>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>Recomendado</label>
              {(() => {
                // Caja de altura fija (como cualquier otro campo de la fila) para que
                // aparezca/desaparezca según el repuesto elegido sin correr los demás
                // campos — es lo que se perdía en el input nativo, que sólo admite texto
                // y no puede repetir el ícono + cantidad que se ve en el desplegable.
                const repCatSel = repuestosCatalogo.find(r => String(r.id) === String(newRep.repuesto));
                const esRecomendado = !!repCatSel?.es_recomendado_equipo;
                return (
                  <div
                    title={esRecomendado ? `Recomendado para este equipo${repCatSel.cantidad_recomendada_equipo ? ` — ${repCatSel.cantidad_recomendada_equipo}` : ''}.` : undefined}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      border: `1px solid ${theme.colors.borderDefault}`,
                      background: theme.colors.bgSecundario,
                      borderRadius: 6, padding: '6px 8px', fontSize: 13, lineHeight: '17px', boxSizing: 'border-box',
                      color: esRecomendado ? theme.colors.success : theme.colors.txtMuted,
                    }}
                  >
                    {esRecomendado ? (
                      <>
                        <i className="bi bi-gear" />
                        <span>{repCatSel.cantidad_recomendada_equipo || 'Sí'}</span>
                      </>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                );
              })()}
            </div>
            <div style={{ flex: '0 0 80px' }}>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>Cant. Planif.</label>
              <ThemedInput type="number" min="1" value={newRep.cantidad_planificada} disabled={locked} onChange={(e) => setNewRep(v => ({ ...v, cantidad_planificada: e.target.value }))} />
            </div>
            <Button onClick={addRepuesto} disabled={locked}>Agregar</Button>
            {!modoMecanico && (
              <Button onClick={() => setShowNuevoRepuesto(true)} disabled={locked}>Nuevo</Button>
            )}
          </div>
          <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 5, marginTop: 8, cursor: form.equipo ? 'pointer' : 'default', opacity: form.equipo ? 1 : 0.5 }} title={!form.equipo ? 'Elegí un equipo primero' : undefined}>
            <input
              type="checkbox"
              checked={soloRecomendados}
              disabled={!form.equipo || locked}
              onChange={(e) => setSoloRecomendados(e.target.checked)}
            />
            Solo recomendados del equipo
          </label>
          {newRep.repuesto && adicionalEntregadoByRepuesto[String(newRep.repuesto)] > 0 ? (
            <div style={{ marginTop: 6, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="bi bi-info-circle-fill"></i>
              {`Este repuesto ya fue entregado como adicional de almacén (${adicionalEntregadoByRepuesto[String(newRep.repuesto)]} u.). Si lo agregás como planificado, la cantidad planificada será independiente de las unidades ya entregadas.`}
            </div>
          ) : null}
        </ZonaAltaRapida>

                <ScrollArea>
          <TableCard>
          <Table>
            <thead>
              <tr>
                <th style={{ minWidth: 200 }}>Repuesto</th>
                <th style={{ minWidth: 140 }}>Tarea</th>
                <th style={{ width: 80 }}>Planif.</th>
                <th style={{ width: 70 }}>Unidad</th>
                <th style={{ width: 90 }}>Recomendado</th>
                {gestionaStockAlmacen && <th style={{ width: 80 }}>Entregado</th>}
                <th style={{ width: 80 }}>Usada</th>
                {!modoMecanico && <th style={{ width: 100 }}>Costo Unit.</th>}
                {!modoMecanico && <th style={{ width: 100 }}>Subtotal</th>}
                <th style={{ width: 78 }}></th>
              </tr>
            </thead>
            <tbody>
              {repuestos.map((r) => {
                const qty = Number(r.cantidad_usada) || 0;
                const cu = Number(r.costo_unitario) || 0;
                const subtotal = qty * cu;
                const tieneErrorStock = !!r._errorStock;
                return (
                <tr key={r.id}>
                  <td>
                    {[r.repuesto_codigo, r.repuesto_marca, r.repuesto_descripcion].filter(Boolean).join(' - ') || '-'}
                  </td>
                  <td>
                    <SearchableSelectField
                      options={tareas}
                      value={r.tarea || ''}
                      onChange={(value) => { setRepuestos(prev => prev.map(x => x.id === r.id ? { ...x, tarea: value || null } : x)); triggerAutoSaveNow(); }}
                      disabled={locked}
                      placeholder="Sin tarea"
                      getOptionValue={(option) => String(option.id)}
                      getOptionLabel={(option) => option.titulo || `Tarea ${option.id}`}
                      getOptionSearch={(option) => `${option.titulo || ''} ${option.descripcion || ''}`}
                      showAllOnFocus
                      size="sm"
                    />
                  </td>
                  <td>
                    <ThemedInput type="number" min="1" style={{ fontSize: 12, padding: '4px 6px' }} value={r.cantidad_planificada ?? 1} disabled={locked} onChange={(e) => { setRepuestos(prev => prev.map(x => x.id === r.id ? { ...x, cantidad_planificada: e.target.value } : x)); scheduleAutoSaveText(); }} />
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12 }}>
                    {r.repuesto_unidad_medida || '—'}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12 }}>
                    {(() => {
                      if (r.cantidad_recomendada_equipo) return r.cantidad_recomendada_equipo;
                      // Fallback para una fila recién agregada, todavía sin recargar
                      // desde el backend: se busca en el catálogo ya cacheado.
                      const repId = String(typeof r.repuesto === 'object' ? r.repuesto?.id : r.repuesto);
                      const repCat = repuestosCatalogo.find(rc => String(rc.id) === repId);
                      return repCat?.cantidad_recomendada_equipo || '-';
                    })()}
                  </td>
                  {gestionaStockAlmacen && (
                  <td style={{ textAlign: 'center', fontSize: 12, fontWeight: 600 }}>
                    {(() => {
                      const repId = String(typeof r.repuesto === 'object' ? r.repuesto?.id : r.repuesto);
                      const entregado = Number(r.cantidad_entregada || 0);
                      const adicional = adicionalEntregadoByRepuesto[repId] || 0;
                      return (
                        <>
                          {entregado}
                          {adicional > 0 ? (
                            <span title={`Además, ${adicional} u. entregadas como adicional de almacén para esta OT`} style={{ marginLeft: 4, fontSize: 10, color: 'inherit', opacity: 0.7, cursor: 'help' }}>
                              (+{adicional} adic.)
                            </span>
                          ) : null}
                        </>
                      );
                    })()}
                  </td>
                  )}
                  <td>
                    <ThemedInput type="number" min="0" style={{ fontSize: 12, padding: '4px 6px' }} value={r.cantidad_usada ?? ''} disabled={locked} $error={tieneErrorStock} title={tieneErrorStock ? 'Cantidad mayor a la entregada por almacén' : undefined} onChange={(e) => { setRepuestos(prev => prev.map(x => x.id === r.id ? { ...x, cantidad_usada: e.target.value, _errorStock: false } : x)); scheduleAutoSaveText(); }} />
                  </td>
                  {!modoMecanico && (
                    <td style={{ textAlign: 'right', fontSize: 12, paddingRight: 8 }}>
                      {r.costo_unitario != null && r.costo_unitario !== '' ? fmtImporte(r.costo_unitario, r.moneda) : '—'}
                    </td>
                  )}
                  {!modoMecanico && (
                    <td style={{ textAlign: 'right', fontSize: 12, fontWeight: 600, paddingRight: 8 }}>
                      {subtotal > 0 ? `$${subtotal.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
                    </td>
                  )}
                  <td>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                      {r.repuesto ? (
                        <EqIconButton
                          className="row-actions"
                          type="button"
                          onClick={() => handleOpenEquivalencias(repuestos.indexOf(r), r.repuesto)}
                          title="Ver equivalencias"
                          disabled={locked}
                        >
                          <i className="bi bi-arrow-left-right" />
                        </EqIconButton>
                      ) : null}
                      <DeleteIconButton
                        className="row-actions"
                        $variant="delete"
                        title="Eliminar repuesto"
                        onClick={() => deleteRepuesto(r.id)}
                        disabled={locked && !canDelete}
                      >
                        <i className="bi bi-trash" />
                      </DeleteIconButton>
                    </div>
                  </td>
                </tr>
                );
              })}
              {repuestos.length === 0 ? (
                <tr>
                  <td colSpan={(modoMecanico ? 8 : 10) - (gestionaStockAlmacen ? 0 : 1)} style={{ textAlign: 'center' }}><MutedText>Sin repuestos cargados.</MutedText></td>
                </tr>
              ) : null}
            </tbody>
          </Table>
          </TableCard>

        {!modoMecanico && (
          <div style={{ marginTop: 10, fontSize: 13 }}>
            <strong>Total repuestos:</strong> {fmtOtc(totalRepuestos)}
          </div>
        )}

        <ReplacementLogWrap>
          <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 700 }}>Registro de reemplazos por falta de stock</div>
          <TableCard>
            <ReplacementLogTable>
              <thead>
                <tr>
                  <th style={{ width: '38%' }}>Repuesto original</th>
                  <th style={{ width: '38%' }}>Repuesto reemplazo</th>
                  <th style={{ width: '12%', textAlign: 'center' }}>Planif.</th>
                  <th style={{ width: '12%', textAlign: 'center' }}>Usada</th>
                </tr>
              </thead>
              <tbody>
                {otcReplacementLogRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center' }}>
                      <MutedText>Sin reemplazos registrados por equivalencia.</MutedText>
                    </td>
                  </tr>
                ) : otcReplacementLogRows.map((logRow, idx) => (
                  <tr key={`otc-repl-${idx}-${logRow.original}-${logRow.reemplazo}`}>
                    <td>{logRow.original}</td>
                    <td>{logRow.reemplazo}</td>
                    <td style={{ textAlign: 'center' }}>{logRow.cantidad_planificada}</td>
                    <td style={{ textAlign: 'center' }}>{logRow.cantidad_usada}</td>
                  </tr>
                ))}
              </tbody>
            </ReplacementLogTable>
          </TableCard>
        </ReplacementLogWrap>
                </ScrollArea>
              </TabBody>
            </TabPanel>

            <TabPanel clave="repuestosAlmacen" $active={activeTab === 'repuestosAlmacen'}>
              <TabBody>
                <ScrollArea>
          <TableCard>
          <Table>
            <thead>
              <tr>
                <th style={{ minWidth: 200 }}>Repuesto</th>
                <th style={{ width: 80 }}>Entregado</th>
                <th style={{ width: 70 }}>Unidad</th>
                <th style={{ width: 80 }}>Usada</th>
                {!modoMecanico && <th style={{ width: 100 }}>Costo Unit.</th>}
                {!modoMecanico && <th style={{ width: 100 }}>Subtotal</th>}
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {repuestosAdicionales.map((r) => {
                const qty = Number(r.cantidad_usada) || 0;
                const cu = Number(r.costo_unitario) || 0;
                const subtotal = qty * cu;
                const tieneErrorStock = !!r._errorStock;
                return (
                <tr key={r.id}>
                  <td>
                    {[r.repuesto_codigo, r.repuesto_marca, r.repuesto_descripcion].filter(Boolean).join(' - ') || '-'}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12, fontWeight: 600 }}>
                    {Number(r.cantidad_entregada || 0)}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 12 }}>
                    {r.repuesto_unidad_medida || '—'}
                  </td>
                  <td>
                    <ThemedInput type="number" min="0" style={{ fontSize: 12, padding: '4px 6px' }} value={r.cantidad_usada ?? ''} disabled={locked} $error={tieneErrorStock} title={tieneErrorStock ? 'Cantidad mayor a la entregada por almacén' : undefined} onChange={(e) => { setRepuestosAdicionales(prev => prev.map(x => x.id === r.id ? { ...x, cantidad_usada: e.target.value, _errorStock: false } : x)); scheduleAutoSaveText(); }} />
                  </td>
                  {!modoMecanico && (
                    <td style={{ textAlign: 'right', fontSize: 12, paddingRight: 8 }}>
                      {r.costo_unitario != null && r.costo_unitario !== '' ? fmtImporte(r.costo_unitario, r.moneda) : '—'}
                    </td>
                  )}
                  {!modoMecanico && (
                    <td style={{ textAlign: 'right', fontSize: 12, fontWeight: 600, paddingRight: 8 }}>
                      {subtotal > 0 ? `$${subtotal.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
                    </td>
                  )}
                  <td>
                    <DeleteIconButton
                      className="row-actions"
                      $variant="delete"
                      title="Eliminar repuesto adicional"
                      onClick={() => deleteRepuestoAdicional(r.id)}
                      disabled={locked && !canDelete}
                    >
                      <i className="bi bi-trash" />
                    </DeleteIconButton>
                  </td>
                </tr>
                );
              })}
              {repuestosAdicionales.length === 0 ? (
                <tr>
                  <td colSpan={modoMecanico ? 5 : 7} style={{ textAlign: 'center' }}><MutedText>Sin repuestos adicionales de almacén.</MutedText></td>
                </tr>
              ) : null}
            </tbody>
          </Table>
          </TableCard>

        {!modoMecanico && (
          <div style={{ marginTop: 10, fontSize: 13 }}>
            <strong>Total repuestos adicionales:</strong> {fmtOtc(totalRepuestosAdicionales)}
          </div>
        )}

        <ReplacementLogWrap>
          <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 700 }}>Registro de reemplazos por falta de stock</div>
          <TableCard>
            <ReplacementLogTable>
              <thead>
                <tr>
                  <th style={{ width: '38%' }}>Repuesto original</th>
                  <th style={{ width: '38%' }}>Repuesto reemplazo</th>
                  <th style={{ width: '12%', textAlign: 'center' }}>Cantidad</th>
                  <th style={{ width: '12%', textAlign: 'center' }}>Usada</th>
                </tr>
              </thead>
              <tbody>
                {repuestosAdicionales.filter(r => r.observaciones && /Reemplazo por equivalencia:/i.test(r.observaciones)).length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center' }}>
                      <MutedText>Sin reemplazos registrados por equivalencia.</MutedText>
                    </td>
                  </tr>
                ) : repuestosAdicionales.filter(r => r.observaciones && /Reemplazo por equivalencia:/i.test(r.observaciones)).map((r, idx) => {
                  const match = r.observaciones.match(/Reemplazo por equivalencia:\s*(.+?)\s*->\s*(.+?)\.?$/i);
                  return match ? (
                    <tr key={`adiq-repl-${idx}`}>
                      <td>{match[1]}</td>
                      <td>{match[2]}</td>
                      <td style={{ textAlign: 'center' }}>{r.cantidad_planificada}</td>
                      <td style={{ textAlign: 'center' }}>{r.cantidad_usada}</td>
                    </tr>
                  ) : null;
                })}
              </tbody>
            </ReplacementLogTable>
          </TableCard>
        </ReplacementLogWrap>
                </ScrollArea>
              </TabBody>
            </TabPanel>

            <TabPanel clave="manoObra" $active={activeTab === 'manoObra'}>
              <TabBody>
                <ZonaAltaRapida as={AddZone} onAgregar={addMecanico} disabled={locked}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>Nombre</label>
              <SearchableCombo
                saltarAlSiguiente={false}
                items={mecanicos}
                value={newMec.mecanico}
                onChange={(v) => {
                  const mecCat = mecanicos.find(m => String(m.id) === String(v));
                  setNewMec(prev => ({
                    ...prev,
                    mecanico: v,
                    costo_hora: prev.costo_hora !== '' ? prev.costo_hora
                      : (mecCat?.costo_hora != null ? String(mecCat.costo_hora) : ''),
                  }));
                }}
                disabled={locked}
                placeholder="Buscar..."
                getLabel={mecanicoLabel}
                getFilterText={mecanicoLabel}
              />
            </div>
            <div style={{ flex: '0 0 84px' }}>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>Horas</label>
              <ThemedInput type="number" step="0.5" min="0.5" value={newMec.horas_trabajadas} disabled={locked} onChange={(e) => setNewMec(v => ({ ...v, horas_trabajadas: e.target.value }))} />
            </div>
            {!modoMecanico && (
              <div style={{ flex: '0 0 100px' }}>
                <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>Costo/Hora</label>
                <div style={{ fontSize: 13, padding: '6px 0' }}>
                  {newMec.costo_hora !== '' && newMec.costo_hora != null ? `$ ${newMec.costo_hora}` : '-'}
                </div>
              </div>
            )}
            <Button onClick={addMecanico} disabled={locked} style={{ flexShrink: 0 }}>Agregar</Button>
            {!modoMecanico && (
              <Button onClick={() => setShowNuevoMecanico(true)} disabled={locked} style={{ flexShrink: 0 }}>Nuevo</Button>
            )}
          </div>
        </ZonaAltaRapida>

                <ScrollArea>
          <TableCard>
          <Table>
            <thead>
              <tr>
                <th>Mecánico</th>
                <th style={{ width: 100 }}>Horas</th>
                {!modoMecanico && <th style={{ width: 110 }}>Costo/Hora</th>}
                {!modoMecanico && <th style={{ width: 110 }}>Subtotal</th>}
                <th style={{ width: 42 }}></th>
              </tr>
            </thead>
            <tbody>
              {otcMecanicos.map((m) => (
                <tr key={m.id}>
                  <td>{m.mecanico_nombre || '-'}</td>
                  <td>
                    <ThemedInput type="number" step="0.5" min="0.5" style={{ fontSize: 12, padding: '4px 6px' }} value={m.horas_trabajadas ?? ''} disabled={locked} onChange={(e) => { setOtcMecanicos(prev => prev.map(x => x.id === m.id ? { ...x, horas_trabajadas: e.target.value } : x)); scheduleAutoSaveText(); }} />
                  </td>
                  {!modoMecanico && (
                    <td style={{ fontSize: 12 }}>
                      {m.costo_hora != null ? `$ ${m.costo_hora}` : '-'}
                    </td>
                  )}
                  {!modoMecanico && (
                    <td style={{ fontSize: 12 }}>
                      {m.horas_trabajadas && m.costo_hora ? `$ ${(Number(m.horas_trabajadas) * Number(m.costo_hora)).toFixed(0)}` : '-'}
                    </td>
                  )}
                  <td>
                    <DeleteIconButton className="row-actions" $variant="delete" title="Eliminar mecánico" onClick={() => deleteMecanico(m.id)} disabled={locked && !canDelete}>
                      <i className="bi bi-trash" />
                    </DeleteIconButton>
                  </td>
                </tr>
              ))}
              {otcMecanicos.length === 0 ? (
                <tr>
                  <td colSpan={modoMecanico ? 3 : 5} style={{ textAlign: 'center' }}><MutedText>Sin mecánicos cargados.</MutedText></td>
                </tr>
              ) : null}
            </tbody>
          </Table>
          </TableCard>
                </ScrollArea>
                {!modoMecanico && (
                  <SubtotalLine>
                    <strong>Costo global mano de obra:</strong> {fmtOtc(totalManoObra)}
                  </SubtotalLine>
                )}
              </TabBody>
            </TabPanel>

            <TabPanel clave="gastos" $active={activeTab === 'gastos'}>
              <TabBody>
                <ZonaAltaRapida as={AddZone} onAgregar={addGastoExtra} disabled={locked}>
          <div style={{ display: 'grid', gridTemplateColumns: modoMecanico ? '2fr 1fr auto' : '2fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>Concepto</label>
              <ThemedInput value={newGasto.concepto} disabled={locked} onChange={(e) => setNewGasto(v => ({ ...v, concepto: e.target.value }))} />
            </div>
            {!modoMecanico && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>Precio Unit.</label>
                <ThemedInput type="number" step="0.01" min="0" value={newGasto.precio_unitario} disabled={locked} onChange={(e) => setNewGasto(v => ({ ...v, precio_unitario: e.target.value }))} />
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>Cantidad</label>
              <ThemedInput type="number" step="1" min="1" value={newGasto.cantidad} disabled={locked} onChange={(e) => setNewGasto(v => ({ ...v, cantidad: e.target.value }))} />
            </div>
            <Button onClick={addGastoExtra} disabled={locked}>Agregar</Button>
          </div>
        </ZonaAltaRapida>

                <ScrollArea>
          <TableCard>
          <Table>
            <thead>
              <tr>
                <th>Concepto</th>
                {!modoMecanico && <th style={{ width: 120 }}>Precio Unit.</th>}
                <th style={{ width: 90 }}>Cantidad</th>
                {!modoMecanico && <th style={{ width: 120 }}>Costo Total</th>}
                <th style={{ width: 42 }}></th>
              </tr>
            </thead>
            <tbody>
              {gastosExtra.map((g) => (
                <tr key={g.id}>
                  <td>
                    <ThemedInput style={{ fontSize: 12, padding: '4px 6px' }} value={g.concepto || ''} disabled={locked} onChange={(e) => { setGastosExtra(prev => prev.map(x => x.id === g.id ? { ...x, concepto: e.target.value } : x)); scheduleAutoSaveText(); }} />
                  </td>
                  {!modoMecanico && (
                    <td>
                      <ThemedInput type="number" step="0.01" min="0" style={{ fontSize: 12, padding: '4px 6px' }} value={g.precio_unitario ?? ''} disabled={locked} onChange={(e) => { setGastosExtra(prev => prev.map(x => x.id === g.id ? { ...x, precio_unitario: e.target.value } : x)); scheduleAutoSaveText(); }} />
                    </td>
                  )}
                  <td>
                    <ThemedInput type="number" step="1" min="1" style={{ fontSize: 12, padding: '4px 6px' }} value={g.cantidad ?? ''} disabled={locked} onChange={(e) => { setGastosExtra(prev => prev.map(x => x.id === g.id ? { ...x, cantidad: e.target.value } : x)); scheduleAutoSaveText(); }} />
                  </td>
                  {!modoMecanico && (
                    <td style={{ fontSize: 12 }}>$ {((Number(g.cantidad) || 0) * (Number(g.precio_unitario) || 0)).toFixed(0)}</td>
                  )}
                  <td>
                    <DeleteIconButton className="row-actions" $variant="delete" title="Eliminar gasto" onClick={() => deleteGastoExtra(g.id)} disabled={locked && !canDelete}>
                      <i className="bi bi-trash" />
                    </DeleteIconButton>
                  </td>
                </tr>
              ))}
              {gastosExtra.length === 0 ? (
                <tr>
                  <td colSpan={modoMecanico ? 3 : 5} style={{ textAlign: 'center' }}><MutedText>Sin gastos extra cargados.</MutedText></td>
                </tr>
              ) : null}
            </tbody>
          </Table>
          </TableCard>
                </ScrollArea>
                {!modoMecanico && (
                  <SubtotalLine>
                    <strong>Total gastos extra:</strong> {fmtOtc(totalGastosExtra)}
                  </SubtotalLine>
                )}
              </TabBody>
            </TabPanel>

            <TabPanel clave="observaciones" $active={activeTab === 'observaciones'}>
              <TabBody>
                <Row>
                  <Field>
                    <label>Descripción del problema</label>
                    <textarea rows={8} value={form.descripcion} disabled={locked} onChange={(e) => { setForm(v => ({ ...v, descripcion: e.target.value })); scheduleAutoSaveText(); }} />
                  </Field>
                  <Field>
                    <label>Observaciones</label>
                    <textarea rows={8} value={form.observaciones} disabled={locked} onChange={(e) => { setForm(v => ({ ...v, observaciones: e.target.value })); scheduleAutoSaveText(); }} />
                  </Field>
                </Row>
              </TabBody>
            </TabPanel>

            <TabPanel clave="adjuntos" $active={activeTab === 'adjuntos'}>
              <TabBody>
                <AdjuntosOtTab
                  registroId={currentOtcId}
                  adjuntos={adjuntos}
                  onSubir={handleSubirAdjunto}
                  onEliminar={handleEliminarAdjunto}
                  onEditar={handleEditarAdjunto}
                  disabled={locked}
                />
              </TabBody>
            </TabPanel>
          </Panel>
        </MainColumn>

        <SideColumn ref={sideColumnRef} $hiddenMobile={activeTab !== 'detalles'}>
          {!modoMecanico && (
            <Panel $shrink={false}>
              <PanelHead>
                <span><i className="bi bi-calculator" />Resumen de costos</span>
                <ChipsMonedaOtc>
                  {MONEDAS_OT.map(({ valor, label }) => (
                    <ChipMonedaOtc
                      key={valor}
                      type="button"
                      $activo={monedaVista === valor}
                      title={`Ver los totales en ${label}`}
                      onClick={() => setMonedaVista(valor)}
                    >
                      {valor === 'USD' ? 'USD' : '$'}
                    </ChipMonedaOtc>
                  ))}
                </ChipsMonedaOtc>
              </PanelHead>
              <PanelBody>
                <SummaryRow><span>Repuestos</span><span>{fmtOtc(totalRepuestos + totalRepuestosAdicionales)}</span></SummaryRow>
                <SummaryRow><span>Mano de obra</span><span>{fmtOtc(totalManoObra)}</span></SummaryRow>
                <SummaryRow><span>Gastos extra</span><span>{fmtOtc(totalGastosExtra)}</span></SummaryRow>
                <SummaryTotalRow><span>Total general OTC</span><span>{fmtOtc(totalGeneral)}</span></SummaryTotalRow>
                {hayMonedasMixtas && (
                  <AvisoMonedaMixta>
                    Esta orden tiene renglones en pesos y en dólares. El total los unifica
                    usando la cotización de la fecha de cada uno.
                  </AvisoMonedaMixta>
                )}
              </PanelBody>
            </Panel>
          )}

          {equipoSeleccionado && (
            <Panel $overflowVisible $shrink={false}>
              <PanelHead>
                <span><i className="bi bi-activity" />Estado del equipo</span>
              </PanelHead>
              <PanelBody>
                <SummaryRow>
                  <span>Estado actual</span>
                  <span style={{ fontWeight: 600 }}>{equipoSeleccionado.estado_nombre || '—'}</span>
                </SummaryRow>
                <div style={{ marginTop: 8 }}>
                  <SearchableCombo
                    items={estadosEquipo}
                    value={estadoEquipoSeleccionado}
                    onChange={(v) => { setEstadoEquipoSeleccionado(v); triggerAutoSaveNow(); }}
                    placeholder="Cambiar estado..."
                    getLabel={e => e.nombre}
                    disabled={locked}
                  />
                </div>
                <MutedText style={{ marginTop: 6 }}>El estado se guarda junto con la OTC.</MutedText>
              </PanelBody>
            </Panel>
          )}

          {currentOtcId && form.estado === 'abierta' ? (
            <Panel $shrink={false} ref={closePanelRef}>
              <PanelHead>
                <span><i className="bi bi-flag" />Estado de la OTC</span>
              </PanelHead>
              <PanelBody>
                {showClosePanel ? (
                  <CierreBody>
                    <InfoItem>
                      <label>Fecha de cierre (DD-MM-AAAA)</label>
                      <ThemedInput
                        type="date"
                        max={todayYmd()}
                        value={closeForm.fecha}
                        onChange={(e) => setCloseForm(v => ({ ...v, fecha: e.target.value }))}
                      />
                    </InfoItem>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={!!closeForm.generarParte}
                        onChange={(e) => setCloseForm(v => ({ ...v, generarParte: e.target.checked }))}
                      />
                      Generar parte diario automático
                    </label>
                    <InfoItem>
                      <label>Lectura real (horómetro/odómetro)</label>
                      <ThemedInput
                        type="number"
                        value={closeForm.odometro}
                        disabled={!closeForm.generarParte}
                        onChange={(e) => setCloseForm(v => ({ ...v, odometro: e.target.value }))}
                      />
                    </InfoItem>
                    <MutedText>Formato visual: {closeForm.fecha ? closeForm.fecha.split('-').reverse().join('-') : ''}</MutedText>
                    <SideActions>
                      <Button $success onClick={() => changeEstado('cerrada')}>Confirmar cierre</Button>
                      <Button onClick={() => setShowClosePanel(false)}>Cancelar</Button>
                    </SideActions>
                  </CierreBody>
                ) : (
                  <SideActions>
                    <Button onClick={() => {
                      setCloseForm({
                        fecha: form.fecha_cierre_planificada || todayYmd(),
                        generarParte: false,
                        odometro: String(equipoSeleccionado?.odometro_actual ?? equipoSeleccionado?.odometro ?? ''),
                      });
                      setShowClosePanel(true);
                      setTimeout(() => {
                        closePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 50);
                    }}>
                      <i className="bi bi-check2-circle" /> Cerrar OTC
                    </Button>
                    <Button $danger onClick={() => changeEstado('cancelada')}>
                      <i className="bi bi-x-circle" /> Cancelar OTC
                    </Button>
                  </SideActions>
                )}
              </PanelBody>
            </Panel>
          ) : null}
        </SideColumn>
      </WorkspaceBody>

      {showEqModal && eqIdx !== null && repuestos[eqIdx] && repuestos[eqIdx].repuesto && (
        <EquivalenciasModal
          repuestoId={repuestos[eqIdx].repuesto}
          obraId={equipoSeleccionado?.obra}
          open={showEqModal}
          onClose={() => setShowEqModal(false)}
          onSelect={handleSelectEquivalente}
        />
      )}

      {showNuevoRepuesto && (
        <ConfiguracionBase
          apiEndpoint="repuestos"
          titulo="Repuestos"
          descripcion=""
          icon="fas fa-tools"
          compact
          modalOnly
          autoOpenAdd
          modalZIndex={1200}
          onRequestClose={() => setShowNuevoRepuesto(false)}
          onRecordSaved={async (savedRecord) => {
            try {
              if (savedRecord?.id != null) {
                // Carga solo el repuesto recién creado y lo pre-selecciona
                const newItem = await apiClient.get(`/repuestos/${savedRecord.id}/`).then(r => r.data).catch(() => null);
                if (newItem) setRepuestosCatalogo([newItem]);
                setNewRep(v => ({ ...v, repuesto: String(savedRecord.id) }));
              }
            } catch (e) { /* noop */ }
            setShowNuevoRepuesto(false);
          }}
        />
      )}

        {showNuevaTarea && (
          <ConfiguracionBase
            apiEndpoint="tareas"
            titulo="Tareas"
            descripcion=""
            icon="fas fa-tasks"
            compact
            modalOnly
            autoOpenAdd
            modalZIndex={1200}
            onRequestClose={() => setShowNuevaTarea(false)}
            onRecordSaved={async (savedRecord) => {
              try {
                // La tarea recién creada se suma a la lista del buscador sin
                // volver a pedir nada: ya la tenemos entera acá.
                if (savedRecord?.id != null) {
                  sumarTareaAlCatalogo(savedRecord);
                  setNewTask({ tarea_catalogo: String(savedRecord.id) });
                  setTaskSearch(savedRecord.nombre || '');
                }
              } catch (e) { /* noop */ }
              setShowNuevaTarea(false);
            }}
          />
        )}

        {showNuevoMecanico && (
          <ConfiguracionBase
            apiEndpoint="mecanicos"
            titulo="Mecánicos"
            descripcion=""
            icon="fas fa-user-cog"
            compact
            modalOnly
            autoOpenAdd
            modalZIndex={1200}
            onRequestClose={() => setShowNuevoMecanico(false)}
            onRecordSaved={async (savedRecord) => {
              try {
                const list = await fetchAllOptions('/mecanicos/', { ordering: 'nombre' });
                setMecanicos(list);
                if (savedRecord?.id != null) {
                  setNewMec(v => ({ ...v, mecanico: String(savedRecord.id) }));
                }
              } catch (e) { /* noop */ }
              setShowNuevoMecanico(false);
            }}
          />
        )}

        {showNuevoEstadoReparacion && (
          <ConfiguracionBase
            apiEndpoint="estados-reparacion"
            titulo="Estados de reparación"
            descripcion=""
            icon="fas fa-tag"
            compact
            modalOnly
            autoOpenAdd
            modalZIndex={1200}
            onRequestClose={() => setShowNuevoEstadoReparacion(false)}
            onRecordSaved={async (savedRecord) => {
              try {
                const list = await fetchAllOptions('/estados-reparacion/', { ordering: 'nombre' });
                setEstadosReparacion(list);
                if (savedRecord?.id != null) {
                  setForm(f => ({ ...f, estado_reparacion: String(savedRecord.id) }));
                }
              } catch (e) { /* noop */ }
              setShowNuevoEstadoReparacion(false);
            }}
          />
        )}

      <PrintOnlyBlock>
        <EquivalenciasPrintTable
          equivalenciasData={equivalenciasCache}
          getOriginalByRepId={getOtcOriginalByRepId}
        />
      </PrintOnlyBlock>
    </WorkspacePage>
  );
};

export default FormularioOTC;
